import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import CreatableSelect from 'react-select/creatable'
import AsyncCreatableSelect from 'react-select/async-creatable'
import Select from 'react-select'
import Barcode from 'react-barcode'
import '../Home/Home.scss'
import '../HomeDetails/HomeDetails.scss'
import './ProductReceiving.scss'
import MobileHeader from '../Common/Header/MobileHeader'
import Header from '../Common/Header/Header'
import useCheckMobileScreen from '../../common/hooks/use-check-mobile-screen-hook'
import SideMenu from '../Common/SideMenu/SideMenu'
import BarcodeIcon from '../../assets/BarcodeIcon'
import SideDrawer from '../Common/SideDrawer/SideDrawer'
import EyeOpen from '../../assets/EyeOpen'
import TrashIcon from '../../assets/TrashIcon'
import AddIcon from '../../assets/AddIcon'
import {
    addProductReceivingHelper,
    updateProductReceivingHelper,
    getInvoiceDetailsHelper,
    getVendorsHelper,
    getSuppliersHelper,
    getUserHelper,
    deleteProductReceivingHelper,
    getMedicalCategoryHelper,
    getProductsHelper,
    getProductDetailsByBarcodeHelper,
    addVendorHelper,
    updateVendorHelper,
    addSuppliersHelper,
    updateSuppliersHelper,
    updateProductDataHelper
} from '../../helper/home-helper'
import { getAllBanksHelper } from '../../helper/bank-details-helper'
import {
    addProductReceivingAlertAction,
    removeProductReceivingAlertAction,
    activeHomeDetailNavigation
} from '../../modules/actions/action'
import {
    BILL_DETAILS_FIELDS,
    PRODUCT_DETAIL_FIELDS,
    PAYMENT_DETAILS_FIELDS,
    PAYMENT_MODE_OPTIONS_PRODUCT_RECEIVING,
    PERMISSION_FEATURE,
    PERMISSION_TYPE,
    HOME_NAVIGATION_ITEMS,
    HOME_CONST
} from '../../common/constant/page-constant'
import { SIDEBAR_CONST } from '../../common/constant/common-constant'
import { formatDate, formatTime } from '../../common/utils/appUtils'
import { saveDraft, loadDraft, clearDraft } from '../../common/utils/storageUtils'
import DRAFT_KEYS from '../../draft/Draft'
import PopupCard from '../Common/PopupCard/PopupCard'

// Helper functions
const getCurrentDateTimeString = () => {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    const hours = String(now.getHours()).padStart(2, '0')
    const minutes = String(now.getMinutes()).padStart(2, '0')
    return `${year}-${month}-${day}T${hours}:${minutes}`
}

const formatDateTime = (dateString, type = 'datetime') => {
    if (!dateString) return ''
    const date = new Date(dateString)
    if (type === 'date') {
        return formatDate(date)
    }
    return `${formatDate(date)} ${formatTime(date)}`
}

// Normalize date to YYYY-MM-DD format for HTML date input
const normalizeToDateInput = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return ''
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

// Generate Vendor ID: First two letters of name + Year + Serial number
const generateVendorId = (vendorName, existingVendors = []) => {
    const year = new Date().getFullYear()
    const prefix = vendorName.substring(0, 2).toUpperCase()
    
    // Find highest serial number for this year
    const yearPrefix = `${prefix}${year}`
    const existingNumbers = existingVendors
        .filter(v => v.vendor_id && v.vendor_id.startsWith(yearPrefix))
        .map(v => {
            const match = v.vendor_id.match(/\d+$/)
            return match ? parseInt(match[0]) : 0
        })
    
    const maxNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) : 0
    const serialNumber = String(maxNumber + 1).padStart(3, '0')
    
    return `${yearPrefix}${serialNumber}`
}

// Generate Supplier ID: Same format as Vendor ID
const generateSupplierId = (supplierName, existingSuppliers = []) => {
    const year = new Date().getFullYear()
    const prefix = supplierName.substring(0, 2).toUpperCase()
    
    // Find highest serial number for this year
    const yearPrefix = `${prefix}${year}`
    const existingNumbers = existingSuppliers
        .filter(s => s.supplier_id && s.supplier_id.startsWith(yearPrefix))
        .map(s => {
            const match = s.supplier_id.match(/\d+$/)
            return match ? parseInt(match[0]) : 0
        })
    
    const maxNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) : 0
    const serialNumber = String(maxNumber + 1).padStart(3, '0')
    
    return `${yearPrefix}${serialNumber}`
}

// Generate Product Barcode: timestamp + random integer (no decimals)
const generateProductBarcode = () => {
    return Date.now() + Math.floor(Math.random() * 1000)
}

// Calculate CPU: (Total TP + Total VAT) / Quantity
const calculateCPU = (totalTP, totalVAT, quantity) => {
    const tp = parseFloat(totalTP) || 0
    const vat = parseFloat(totalVAT) || 0
    const qty = parseFloat(quantity) || 1
    
    if (qty === 0) return 0
    
    return ((tp + vat) / qty).toFixed(2)
}

const ProductReceivingForm = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { id } = useParams()
    const dispatch = useDispatch()
    const isMobile = useCheckMobileScreen()
    const loginData = useSelector((state) => state.loginData)
    const permissions = useSelector((state) => state.loginData?.permissions)

    const isEditMode = !!id
    // Use different draft keys for new mode vs edit mode (per invoice ID)
    const DRAFT_KEY = isEditMode 
        ? `${DRAFT_KEYS.PRODUCT_RECEIVING}_edit_${id}` 
        : DRAFT_KEYS.PRODUCT_RECEIVING
    
    // Track if initial data load is complete (for edit mode) or draft restore is complete (for new mode)
    const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false)
    
    // Order Book data from navigation state
    const orderBookData = location.state
    const isFromOrderBook = orderBookData?.fromOrderBook === true
    
    // Track if Order Book data has been populated (to prevent re-running useEffect)
    const [orderBookPopulated, setOrderBookPopulated] = useState(false)

    // Section 1: Invoice & Party Information
    const [invoiceNumber, setInvoiceNumber] = useState('')
    const [invoiceDate, setInvoiceDate] = useState('')
    const [selectedVendor, setSelectedVendor] = useState(null)
    const [vendorContact, setVendorContact] = useState('')
    const [vendorCountryCode, setVendorCountryCode] = useState('+880')
    const [vendorAddress, setVendorAddress] = useState('')
    const [selectedSupplier, setSelectedSupplier] = useState(null)
    const [supplierContact, setSupplierContact] = useState('')
    const [supplierCountryCode, setSupplierCountryCode] = useState('+880')
    const [supplierAddress, setSupplierAddress] = useState('')
    const [notes, setNotes] = useState('')

    // Dropdown options
    const [vendorOptions, setVendorOptions] = useState([])
    const [supplierOptions, setSupplierOptions] = useState([])
    const [categoryOptions, setCategoryOptions] = useState([])
    
    // Store full vendor and supplier data for ID generation
    const [allVendors, setAllVendors] = useState([])
    const [allSuppliers, setAllSuppliers] = useState([])

    // Section 2: Product Details
    const [selectedProduct, setSelectedProduct] = useState(null)
    const [productId, setProductId] = useState('')
    const [productName, setProductName] = useState('')
    const [genericName, setGenericName] = useState('')
    const [strength, setStrength] = useState('')
    const [dose, setDose] = useState('')
    const [category, setCategory] = useState('')
    const [barcode, setBarcode] = useState('')
    const [batchNumber, setBatchNumber] = useState('')
    const [expiryDate, setExpiryDate] = useState('')
    const [quantity, setQuantity] = useState('')
    const [bonusQuantity, setBonusQuantity] = useState('')
    const [sizeOrMedType, setSizeOrMedType] = useState('Size') // 'Size' or 'Med/Strip'
    const [sizeOrMedValue, setSizeOrMedValue] = useState('')
    const [totalTP, setTotalTP] = useState('')
    const [totalVAT, setTotalVAT] = useState('')
    const [costPerUnit, setCostPerUnit] = useState('')
    const [ratePerUnit, setRatePerUnit] = useState('')
    const [productType, setProductType] = useState('Pharmaceutical')
    const [prescriptionRequired, setPrescriptionRequired] = useState('Yes')
    const [prescriptionDetails, setPrescriptionDetails] = useState('')
    const [origin, setOrigin] = useState('Local')

    // Product list options
    const [productOptions, setProductOptions] = useState([])
    const [productPage, setProductPage] = useState(0)
    const [hasMoreProducts, setHasMoreProducts] = useState(true)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const PRODUCTS_PER_PAGE = 100

    // Section 3: Products Table
    const [productsInTable, setProductsInTable] = useState([])
    const [selectedTableRowIndex, setSelectedTableRowIndex] = useState(null)

    // Section 4: Invoice Summary
    const [tradeDiscountPercentage, setTradeDiscountPercentage] = useState(0)
    const [tradeDiscountAmount, setTradeDiscountAmount] = useState(0)
    const [paymentMode, setPaymentMode] = useState('CASH_IN_HAND')
    const [banksList, setBanksList] = useState([])
    const [selectedBank, setSelectedBank] = useState(null)

    // Calculated values
    const [invoiceValue, setInvoiceValue] = useState(0)
    const [totalTPValue, setTotalTPValue] = useState(0)
    const [totalVATValue, setTotalVATValue] = useState(0)
    const [netValue, setNetValue] = useState(0)

    // Loading states
    const [loading, setLoading] = useState(false)
    const [loadingInvoice, setLoadingInvoice] = useState(false)

    // Product receiving ID for edit mode
    const [productReceivingId, setProductReceivingId] = useState(null)
    const [currentInvoiceStatus, setCurrentInvoiceStatus] = useState('HOLD')
    
    // Order Book ID when coming from Order Book
    const [orderBookId, setOrderBookId] = useState(null)

    // Barcode drawer state
    const [barcodeDrawerOpen, setBarcodeDrawerOpen] = useState(false)
    const svgRef = useRef(null)

    // Refs for keyboard navigation (Enter key to move to next field)
    const invoiceNumberRef = useRef(null)
    const vendorRef = useRef(null)
    const vendorCountryCodeRef = useRef(null)
    const vendorContactRef = useRef(null)
    const vendorAddressRef = useRef(null)
    const supplierRef = useRef(null)
    const supplierCountryCodeRef = useRef(null)
    const supplierContactRef = useRef(null)
    const supplierAddressRef = useRef(null)
    const productNameRef = useRef(null)
    const genericNameRef = useRef(null)
    const sizeOrMedValueRef = useRef(null)
    const strengthRef = useRef(null)
    const categoryRef = useRef(null)
    const barcodeInputRef = useRef(null)
    const batchNumberRef = useRef(null)
    const expiryDateRef = useRef(null)
    const quantityRef = useRef(null)
    const bonusQuantityRef = useRef(null)
    const totalTPRef = useRef(null)
    const totalVATRef = useRef(null)
    const rpuRef = useRef(null)
    const skipNextRecalculationRef = useRef(false)
    const productSearchTimerRef = useRef(null)
    const productSearchAbortRef = useRef(null)
    const productSearchResolveRef = useRef(null)

    // Confirmation dialog states
    const [showHoldConfirmation, setShowHoldConfirmation] = useState(false)
    const [showConfirmConfirmation, setShowConfirmConfirmation] = useState(false)
    const [showCloseConfirmation, setShowCloseConfirmation] = useState(false)

    // Dropdown menu open states for Enter key navigation
    const [vendorMenuOpen, setVendorMenuOpen] = useState(false)
    const [supplierMenuOpen, setSupplierMenuOpen] = useState(false)
    const [productMenuOpen, setProductMenuOpen] = useState(false)
    const [categoryMenuOpen, setCategoryMenuOpen] = useState(false)

    useEffect(() => {
        loadVendors()
        loadSuppliers()
        loadCategories()
        loadBanks()

        if (!isEditMode) {
            // Set default invoice date for new invoice
            setInvoiceDate(getCurrentDateTimeString())
        }
    }, [id])

    // Helper function to restore draft data
    const restoreDraftData = (draft) => {
        // Section 1: Invoice & Party Information
        if (draft.invoiceNumber) setInvoiceNumber(draft.invoiceNumber)
        if (draft.invoiceDate) setInvoiceDate(draft.invoiceDate)
        if (draft.selectedVendor) setSelectedVendor(draft.selectedVendor)
        if (draft.vendorContact) setVendorContact(draft.vendorContact)
        if (draft.vendorCountryCode) setVendorCountryCode(draft.vendorCountryCode)
        if (draft.vendorAddress) setVendorAddress(draft.vendorAddress)
        if (draft.selectedSupplier) setSelectedSupplier(draft.selectedSupplier)
        if (draft.supplierContact) setSupplierContact(draft.supplierContact)
        if (draft.supplierCountryCode) setSupplierCountryCode(draft.supplierCountryCode)
        if (draft.supplierAddress) setSupplierAddress(draft.supplierAddress)
        if (draft.notes) setNotes(draft.notes)
        // Section 2: Product Details (form fields)
        if (draft.selectedProduct) setSelectedProduct(draft.selectedProduct)
        if (draft.productId) setProductId(draft.productId)
        if (draft.productName) setProductName(draft.productName)
        if (draft.genericName) setGenericName(draft.genericName)
        if (draft.strength) setStrength(draft.strength)
        if (draft.dose) setDose(draft.dose)
        if (draft.category) setCategory(draft.category)
        if (draft.barcode) setBarcode(draft.barcode)
        if (draft.batchNumber) setBatchNumber(draft.batchNumber)
        if (draft.expiryDate) setExpiryDate(draft.expiryDate)
        if (draft.quantity) setQuantity(draft.quantity)
        if (draft.bonusQuantity) setBonusQuantity(draft.bonusQuantity)
        if (draft.sizeOrMedType) setSizeOrMedType(draft.sizeOrMedType)
        if (draft.sizeOrMedValue) setSizeOrMedValue(draft.sizeOrMedValue)
        if (draft.totalTP) setTotalTP(draft.totalTP)
        if (draft.totalVAT) setTotalVAT(draft.totalVAT)
        if (draft.costPerUnit) setCostPerUnit(draft.costPerUnit)
        if (draft.ratePerUnit) setRatePerUnit(draft.ratePerUnit)
        if (draft.productType) setProductType(draft.productType)
        if (draft.prescriptionRequired) setPrescriptionRequired(draft.prescriptionRequired)
        if (draft.prescriptionDetails) setPrescriptionDetails(draft.prescriptionDetails)
        if (draft.origin) setOrigin(draft.origin)
        // Section 3: Products Table
        if (draft.productsInTable) setProductsInTable(draft.productsInTable)
        // Section 4: Invoice Summary
        if (draft.tradeDiscountPercentage !== undefined) setTradeDiscountPercentage(draft.tradeDiscountPercentage)
        if (draft.tradeDiscountAmount !== undefined) setTradeDiscountAmount(draft.tradeDiscountAmount)
        if (draft.paymentMode) setPaymentMode(draft.paymentMode)
        if (draft.selectedBank) setSelectedBank(draft.selectedBank)
    }

    // Restore draft on mount (for new mode)
    useEffect(() => {
        if (!isEditMode) {
            const draft = loadDraft(DRAFT_KEY)
            if (draft) {
                restoreDraftData(draft)
            }
            // Mark initial load/restore as complete so draft saving can begin
            setIsInitialLoadComplete(true)
        }
    }, [isEditMode])

    // Persist draft on state change (for both new and edit mode)
    // Only save after initial load/restore is complete to avoid overwriting with empty data
    useEffect(() => {
        if (isInitialLoadComplete) {
            saveDraft(DRAFT_KEY, {
                // Section 1: Invoice & Party Information
                invoiceNumber,
                invoiceDate,
                selectedVendor,
                vendorContact,
                vendorCountryCode,
                vendorAddress,
                selectedSupplier,
                supplierContact,
                supplierCountryCode,
                supplierAddress,
                notes,
                // Section 2: Product Details (form fields)
                selectedProduct,
                productId,
                productName,
                genericName,
                strength,
                dose,
                category,
                barcode,
                batchNumber,
                expiryDate,
                quantity,
                bonusQuantity,
                sizeOrMedType,
                sizeOrMedValue,
                totalTP,
                totalVAT,
                costPerUnit,
                ratePerUnit,
                productType,
                prescriptionRequired,
                prescriptionDetails,
                origin,
                // Section 3: Products Table
                productsInTable,
                // Section 4: Invoice Summary
                tradeDiscountPercentage,
                tradeDiscountAmount,
                paymentMode,
                selectedBank
            })
        }
    }, [
        isEditMode,
        isInitialLoadComplete,
        invoiceNumber,
        invoiceDate,
        selectedVendor,
        vendorContact,
        vendorCountryCode,
        vendorAddress,
        selectedSupplier,
        supplierContact,
        supplierCountryCode,
        supplierAddress,
        notes,
        selectedProduct,
        productId,
        productName,
        genericName,
        strength,
        dose,
        category,
        barcode,
        batchNumber,
        expiryDate,
        quantity,
        bonusQuantity,
        sizeOrMedType,
        sizeOrMedValue,
        totalTP,
        totalVAT,
        costPerUnit,
        ratePerUnit,
        productType,
        prescriptionRequired,
        prescriptionDetails,
        origin,
        productsInTable,
        tradeDiscountPercentage,
        tradeDiscountAmount,
        paymentMode,
        selectedBank
    ])

    // Load invoice data only after vendor and supplier options are loaded (for edit mode)
    useEffect(() => {
        if (isEditMode && vendorOptions.length > 0 && supplierOptions.length > 0) {
            loadInvoiceData()
        }
    }, [isEditMode, vendorOptions.length, supplierOptions.length, id])

    // Populate form when coming from Order Book (only once)
    useEffect(() => {
        // Skip if already populated to prevent overwriting user edits
        if (orderBookPopulated) return
        
        if (isFromOrderBook && supplierOptions.length > 0 && orderBookData) {
            // Mark as populated to prevent re-running
            setOrderBookPopulated(true)
            
            // Set Order Book ID
            setOrderBookId(orderBookData.orderBookId)
            
            // Set Supplier (Pharma Company) in Invoice Information section
            const supplierData = orderBookData.supplierData
            let supplierName = ''
            if (supplierData) {
                // Convert to string for comparison since option values are strings
                const matchedSupplier = supplierOptions.find(s => s.value === String(supplierData.id))
                if (matchedSupplier) {
                    setSelectedSupplier(matchedSupplier)
                    setSupplierContact(matchedSupplier.contact || supplierData.mobile_no || '')
                    setSupplierAddress(matchedSupplier.address || supplierData.address || '')
                    supplierName = matchedSupplier.label || supplierData.name || ''
                    if (matchedSupplier.country_code) {
                        setSupplierCountryCode(matchedSupplier.country_code)
                    }
                }
            }
            
            // Set Notes with Order Book reference
            setNotes(`Order Book: ${orderBookData.orderNumber || orderBookData.orderBookId}`)
            
            // Auto-populate Product Information table with all products from Order Book
            const productArray = orderBookData.productArray
            if (productArray && productArray.length > 0) {
                const productsToAdd = productArray.map((product, index) => {
                    const qty = parseInt(product.ordered_quantity || product.quantity) || 0
                    const cpu = parseFloat(product.cost_per_unit) || 0
                    const rpu = parseFloat(product.rate_per_unit) || 0
                    const subtotal = qty * cpu
                    
                    // Find matching product in productOptions
                    const matchedProduct = productOptions.find(p => p.value === String(product.id))
                    
                    // Use matched product data if available, otherwise use order book data
                    const productName = matchedProduct?.label || product.name || ''
                    const genericName = matchedProduct?.genericName || product.generic_name || ''
                    const strength = matchedProduct?.strength || product.strength || ''
                    const dose = matchedProduct?.dose || product.dose || product.size || ''
                    const category = matchedProduct?.category || product.category || ''
                    const barcode = matchedProduct?.barcode || product.barcode || ''
                    const sizeOrMedValue = matchedProduct?.medPerStrip || product.medicine_per_strips || product.size || dose || strength || ''
                    // Default to 'Med/Strip' if no size value available (to avoid validation error)
                    const hasSizeValue = sizeOrMedValue && sizeOrMedValue.toString().trim() !== ''
                    const sizeOrMedType = hasSizeValue ? (product.medicine_per_strips ? 'Med/Strip' : 'Size') : 'Med/Strip'
                    
                    // Calculate VAT
                    const tpValue = parseFloat(product.tp_per_unit) * qty || 0
                    const vatValue = parseFloat(product.vat_per_unit) * qty || 0
                    const vatPercentageValue = tpValue > 0 ? (vatValue / tpValue) * 100 : 0
                    
                    return {
                        id: product.id || null,
                        productId: product.id || `order_${index}`,
                        productName: productName,
                        genericName: genericName,
                        strength: strength,
                        dose: dose,
                        category: category,
                        barcode: barcode,
                        batchNumber: '', // To be filled by user
                        expiryDate: '', // To be filled by user
                        quantity: qty,
                        bonusQuantity: 0,
                        sizeOrMedType: sizeOrMedType,
                        sizeOrMedValue: sizeOrMedValue,
                        medPerStrip: 1,
                        totalTP: tpValue,
                        totalVAT: vatValue,
                        vatPercentage: parseFloat(vatPercentageValue.toFixed(2)),
                        costPerUnit: cpu,
                        ratePerUnit: rpu,
                        productType: product.type || 'Pharmaceutical',
                        prescriptionRequired: product.is_prescription_required || false,
                        prescriptionDetails: '',
                        origin: product.origin || 'Local',
                        subtotal: subtotal,
                        supplierName: supplierName,
                        __isNew__: false
                    }
                })
                
                // Add all products to the table
                setProductsInTable(productsToAdd)
                
                // Pre-populate form fields with the first product for easy editing
                const firstProduct = productArray[0]
                const qty = parseInt(firstProduct.ordered_quantity || firstProduct.quantity) || 0
                const matchedProduct = productOptions.find(p => p.value === String(firstProduct.id))
                
                // Get size/med values
                let formSizeOrMedValue = ''
                let formDose = ''
                let formStrength = ''
                
                if (matchedProduct) {
                    setSelectedProduct(matchedProduct)
                    setProductName(matchedProduct.label || firstProduct.name || '')
                    setGenericName(matchedProduct.genericName || firstProduct.generic_name || '')
                    formStrength = matchedProduct.strength || firstProduct.strength || ''
                    setStrength(formStrength)
                    formDose = matchedProduct.dose || firstProduct.dose || ''
                    setDose(formDose)
                    setCategory(matchedProduct.category || firstProduct.category || '')
                    setBarcode(matchedProduct.barcode || firstProduct.barcode || '')
                    setCostPerUnit(parseFloat(matchedProduct.costPerUnit) || parseFloat(firstProduct.cost_per_unit) || 0)
                    setRatePerUnit(parseFloat(matchedProduct.ratePerUnit) || parseFloat(firstProduct.rate_per_unit) || 0)
                    formSizeOrMedValue = matchedProduct.medPerStrip || firstProduct.medicine_per_strips || firstProduct.size || formDose || formStrength || ''
                    setSizeOrMedValue(formSizeOrMedValue)
                } else {
                    setProductName(firstProduct.name || '')
                    setGenericName(firstProduct.generic_name || '')
                    formStrength = firstProduct.strength || ''
                    setStrength(formStrength)
                    formDose = firstProduct.dose || ''
                    setDose(formDose)
                    setCategory(firstProduct.category || '')
                    setBarcode(firstProduct.barcode || '')
                    setCostPerUnit(parseFloat(firstProduct.cost_per_unit) || 0)
                    setRatePerUnit(parseFloat(firstProduct.rate_per_unit) || 0)
                    formSizeOrMedValue = firstProduct.medicine_per_strips || firstProduct.size || formDose || formStrength || ''
                    setSizeOrMedValue(formSizeOrMedValue)
                }
                
                setProductId(firstProduct.id || '')
                setProductType(firstProduct.type || 'Pharmaceutical')
                setQuantity(qty)
                setTotalTP(parseFloat(firstProduct.tp_per_unit) * qty || 0)
                setTotalVAT(parseFloat(firstProduct.vat_per_unit) * qty || 0)
                // Default to 'Med/Strip' if no size value available (to avoid validation error)
                const formHasSizeValue = formSizeOrMedValue && formSizeOrMedValue.toString().trim() !== ''
                setSizeOrMedType(formHasSizeValue ? (firstProduct.medicine_per_strips ? 'Med/Strip' : 'Size') : 'Med/Strip')
                setOrigin(firstProduct.origin || 'Local')
            }
        }
    }, [isFromOrderBook, supplierOptions, orderBookData, orderBookPopulated])

    // Auto-focus on Invoice Number when page loads (new mode only)
    useEffect(() => {
        if (!isEditMode && !isFromOrderBook) {
            setTimeout(() => {
                invoiceNumberRef.current?.focus()
            }, 300)
        }
    }, [isEditMode, isFromOrderBook])

    // Handle Enter key navigation - move to next field
    const handleFieldKeyDown = (e, nextRef) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            if (nextRef?.current) {
                nextRef.current.focus()
            }
        }
    }

    // Handle Enter key for the last field (RPU) - trigger Add Product
    const handleLastFieldKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleAddProduct()
        }
    }

    // Handle Enter key for Select/CreatableSelect dropdowns
    // First Enter: select from dropdown, Second Enter: move to next field
    const handleSelectKeyDown = (e, nextRef, menuIsOpen) => {
        if (e.key === 'Enter' && !menuIsOpen) {
            e.preventDefault()
            if (nextRef?.current) {
                nextRef.current.focus()
            }
        }
    }

    // Clear product options and reset pagination when supplier changes
    useEffect(() => {
        setProductOptions([])
        setProductPage(0)
        setHasMoreProducts(true)
    }, [selectedSupplier])

    // Auto-calculate CPU when Total TP, Total VAT, or Quantity changes
    useEffect(() => {
        // Check if we have valid values (totalVAT can be 0, which is valid)
        const hasValidTP = totalTP !== '' && totalTP !== null && totalTP !== undefined
        const hasValidVAT = totalVAT !== '' && totalVAT !== null && totalVAT !== undefined
        const hasValidQty = quantity && parseFloat(quantity) > 0
        
        if (hasValidTP && hasValidVAT && hasValidQty) {
            const calculatedCPU = calculateCPU(totalTP, totalVAT, quantity)
            setCostPerUnit(calculatedCPU)
        }
    }, [totalTP, totalVAT, quantity])

    // Auto-calculate invoice totals when products change (recalculate discount from percentage)
    // In edit mode, skip the first recalculation to preserve backend values
    useEffect(() => {
        // Skip recalculation if flag is set (after initial load in edit mode)
        if (skipNextRecalculationRef.current) {
            return
        }
        calculateInvoiceTotals(true)
    }, [productsInTable])

    // Recalculate ONLY net value when discount changes
    // Don't recalculate Invoice Value, Total TP, Total VAT - those should stay the same
    useEffect(() => {
        if (skipNextRecalculationRef.current) {
            return
        }
        // Only update Net Value = Invoice Value - Trade Discount Amount
        const netAmount = invoiceValue - tradeDiscountAmount
        setNetValue(netAmount)
    }, [tradeDiscountAmount, invoiceValue])

    // Requirement #11 & #12: Keyboard handlers for Enter (update) and Delete keys
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Requirement #11: Press Enter to update product details
            if (event.key === 'Enter' && selectedTableRowIndex !== null && productName) {
                event.preventDefault()
                handleSaveProduct()
            }
            
            // Requirement #12: Press Delete to remove selected field/product
            if (event.key === 'Delete' && selectedTableRowIndex !== null) {
                const activeElement = document.activeElement
                const isInputField = activeElement.tagName === 'INPUT' || 
                                   activeElement.tagName === 'TEXTAREA' ||
                                   activeElement.tagName === 'SELECT'
                
                // Only delete product if not typing in an input field
                if (!isInputField) {
                    event.preventDefault()
                    handleDeleteProduct()
                }
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [selectedTableRowIndex, productName, productsInTable]) // Re-attach when these change

    // Helper function to parse contact and extract country code
    const parseContact = (fullContact, defaultCountryCode = '+880') => {
        if (!fullContact) {
            return { countryCode: defaultCountryCode, phoneNumber: '' }
        }
        
        // Check if contact starts with common country codes
        const countryCodes = ['+880', '+1', '+44', '+91', '+86'] // Add more as needed
        for (const code of countryCodes) {
            if (fullContact.startsWith(code)) {
                return {
                    countryCode: code,
                    phoneNumber: fullContact.substring(code.length)
                }
            }
        }
        
        // If no country code found, return default
        return { countryCode: defaultCountryCode, phoneNumber: fullContact }
    }

    const loadVendors = async () => {
        try {
            const response = await getVendorsHelper()
            // Handle both response.data.vendors and direct array response
            const vendors = response?.data?.vendors || response?.vendors || response || []
            if (Array.isArray(vendors)) {
                setAllVendors(vendors) // Store full data for ID generation
                const options = vendors
                    .filter(vendor => vendor && (vendor.id || vendor.id === 0) && vendor.name)
                    .map((vendor) => {
                        // Parse the full contact to extract country code and phone number
                        const { countryCode, phoneNumber } = parseContact(vendor.contact)
                        return {
                            value: String(vendor.id),
                            label: String(vendor.name),
                            contact: phoneNumber, // Just the phone number without country code
                            address: String(vendor.address || ''),
                            country_code: countryCode,
                            vendor_id: String(vendor.vendor_id || '')
                        }
                    })
                setVendorOptions(options)
            }
        } catch (error) {        }
    }

    const loadSuppliers = async () => {
        try {
            const response = await getSuppliersHelper()
            // Handle both response.data.suppliers and direct array response
            const suppliers = response?.data?.suppliers || response || []
            if (Array.isArray(suppliers)) {
                setAllSuppliers(suppliers) // Store full data for ID generation
                const options = suppliers
                    .filter(supplier => supplier && (supplier.id || supplier.id === 0) && supplier.name)
                    .map((supplier) => {
                        // Parse the full contact to extract country code and phone number
                        const { countryCode, phoneNumber } = parseContact(supplier.mobile_no || supplier.contact)
                        return {
                            value: String(supplier.id),
                            label: String(supplier.name),
                            displayName: String(supplier.name),
                            contact: phoneNumber, // Just the phone number without country code
                            address: String(supplier.address || ''),
                            country_code: countryCode,
                            is_approved: supplier.is_approved,
                            supplier_id: String(supplier.supplier_id || '')
                        }
                    })
                setSupplierOptions(options)
            }
        } catch (error) {        }
    }

    const mapProductsToOptions = (products) => {
        return products
            .filter(product => product && (product.id || product.id === 0))
            .map((product) => {
                const name = product.name || product.product_name || 'Unnamed Product'
                return {
                    value: String(product.id),
                    label: String(name),
                    genericName: String(product.generic_name || ''),
                    strength: String(product.strength || ''),
                    dose: String(product.dose || ''),
                    category: typeof product.category === 'string' ? product.category : (product.category?.label || product.category?.value || ''),
                    barcode: String(product.barcode || ''),
                    costPerUnit: String(product.cost_per_unit || ''),
                    ratePerUnit: String(product.rate_per_unit || ''),
                    medPerStrip: String(product.medicine_per_strip || ''),
                    supplier_id: String(product.supplier_id || ''),
                    supplier_name: String(product.supplier_name || '')
                }
            })
            .sort((a, b) => a.label.localeCompare(b.label))
    }

    const loadInitialProducts = () => {
        const params = { limit: PRODUCTS_PER_PAGE, offset: 0, order_by: 'name_asc' }
        if (selectedSupplier?.value) params.supplier_id = selectedSupplier.value
        getProductsHelper(params).then(response => {
            const products = response?.products || response || []
            if (Array.isArray(products)) {
                const options = mapProductsToOptions(products)
                setProductOptions(options)
                setProductPage(1)
                setHasMoreProducts(products.length >= PRODUCTS_PER_PAGE)
            }
        }).catch(() => {})
    }

    const loadMoreProducts = () => {
        if (!hasMoreProducts || isLoadingMore) return
        setIsLoadingMore(true)
        const params = {
            limit: PRODUCTS_PER_PAGE,
            offset: productPage * PRODUCTS_PER_PAGE,
            order_by: 'name_asc'
        }
        if (selectedSupplier?.value) params.supplier_id = selectedSupplier.value
        getProductsHelper(params).then(response => {
            const products = response?.products || response || []
            if (Array.isArray(products)) {
                const options = mapProductsToOptions(products)
                setProductOptions(prev => [...prev, ...options])
                setProductPage(prev => prev + 1)
                setHasMoreProducts(products.length >= PRODUCTS_PER_PAGE)
            }
            setIsLoadingMore(false)
        }).catch(() => setIsLoadingMore(false))
    }

    const loadProductOptions = (inputValue) => {
        if (productSearchAbortRef.current) {
            productSearchAbortRef.current.abort()
        }
        if (productSearchTimerRef.current) {
            clearTimeout(productSearchTimerRef.current)
        }
        if (productSearchResolveRef.current) {
            productSearchResolveRef.current([])
            productSearchResolveRef.current = null
        }

        if (!inputValue) {
            return Promise.resolve(productOptions)
        }

        return new Promise((resolve) => {
            productSearchResolveRef.current = resolve

            productSearchTimerRef.current = setTimeout(() => {
                const abortController = new AbortController()
                productSearchAbortRef.current = abortController
                productSearchResolveRef.current = null

                const params = { search_text: inputValue }
                if (selectedSupplier?.value) params.supplier_id = selectedSupplier.value

                getProductsHelper(params, abortController.signal).then(response => {
                    if (abortController.signal.aborted) return
                    const products = response?.products || response || []
                    if (Array.isArray(products)) {
                        const options = mapProductsToOptions(products)
                        setProductOptions(options)
                        resolve(options)
                    } else {
                        resolve([])
                    }
                }).catch(() => {
                    if (!abortController.signal.aborted) resolve([])
                })
            }, 500)
        })
    }

    const loadCategories = async () => {
        try {
            const response = await getMedicalCategoryHelper()
            // Handle both response.data.medical_categories and direct array response
            const categories = response?.data?.medical_categories || response || []
            if (Array.isArray(categories)) {
                const options = categories.map((cat) => {
                    // Ensure category is a string
                    const categoryValue = typeof cat === 'string' ? cat : (cat?.label || cat?.value || String(cat))
                    return {
                        value: categoryValue,
                        label: categoryValue
                    }
                })
                setCategoryOptions(options)
            }
        } catch (error) {
            // Handle error silently
        }
    }

    const loadBanks = async () => {
        getAllBanksHelper()
            .then(res => {
                setBanksList(res || [])
            })
            .catch(err => {
                console.log('Error loading banks: ', err)
                setBanksList([])
            })
    }

    const loadInvoiceData = async () => {
        try {
            setLoadingInvoice(true)
            const response = await getInvoiceDetailsHelper(id)

            // Handle both response.data and direct response
            const data = response?.data || response
            
            if (data) {
                // Populate Section 1
                setInvoiceNumber(data.invoice_number || '')
                setInvoiceDate(data.date_of_bill || data.invoice_date || '')
                setProductReceivingId(data.id)
                setCurrentInvoiceStatus(data.status || 'HOLD')

                // Vendor
                if (data.vendor_details || data.vendor || data.vendor_id) {
                    const vendor = data.vendor_details || data.vendor || {}
                    // Find vendor from options to get full vendor data
                    const vendorOption = vendorOptions.find(v => 
                        String(v.value) === String(vendor.id) || 
                        String(v.value) === String(data.vendor_id)
                    )
                    
                    if (vendorOption || vendor.id || vendor.name) {
                        setSelectedVendor({
                            value: String(vendor.id || vendorOption?.value || data.vendor_id || ''),
                            label: String(vendor.name || vendorOption?.label || ''),
                            displayName: String(vendor.name || vendorOption?.displayName || ''),
                            vendor_id: String(vendor.vendor_id || vendorOption?.vendor_id || ''),
                            contact: String(vendor.contact || vendorOption?.contact || ''),
                            address: String(vendor.address || vendorOption?.address || ''),
                            country_code: String(vendor.country_code || vendorOption?.country_code || '+880')
                        })
                        setVendorContact(vendor.contact || vendorOption?.contact || '')
                        setVendorCountryCode(vendor.country_code || vendorOption?.country_code || '+880')
                        setVendorAddress(vendor.address || vendorOption?.address || '')
                    }
                }

                // Supplier
                if (data.pharma_details || data.pharmacy_supplier || data.supplier_id) {
                    const supplier = data.pharma_details || data.pharmacy_supplier || {}
                    // Find supplier from options to get full supplier data
                    const supplierOption = supplierOptions.find(s => 
                        String(s.value) === String(supplier.id) || 
                        String(s.value) === String(data.supplier_id)
                    )
                    
                    if (supplierOption || supplier.id || supplier.name) {
                        setSelectedSupplier({
                            value: String(supplier.id || supplierOption?.value || data.supplier_id || ''),
                            label: String(supplier.name || supplierOption?.label || ''),
                            displayName: String(supplier.name || supplierOption?.displayName || ''),
                            supplier_id: String(supplier.supplier_id || supplierOption?.supplier_id || ''),
                            contact: String(supplier.mobile_no || supplier.contact || supplierOption?.contact || ''),
                            address: String(supplier.address || supplierOption?.address || ''),
                            country_code: String(supplier.country_code || supplierOption?.country_code || '+880'),
                            is_approved: supplier.is_approved ?? supplierOption?.is_approved ?? false
                        })
                        setSupplierContact(supplier.mobile_no || supplier.contact || supplierOption?.contact || '')
                        setSupplierCountryCode(supplier.country_code || supplierOption?.country_code || '+880')
                        setSupplierAddress(supplier.address || supplierOption?.address || '')
                    }
                }

                setNotes(data.notes || '')

                // Populate Section 3 - Products Table
                const productDetails = data.product_details || data.product_receiving_details || []
                const supplierName = (data.pharma_details || data.pharmacy_supplier)?.name || ''
                
                // Set flag to skip recalculation when products are loaded
                // We'll use backend values for invoice summary instead
                skipNextRecalculationRef.current = true
                
                if (productDetails && productDetails.length > 0) {
                    // Get invoice-level totals for calculating product TP/VAT
                    const invoiceAmount = parseFloat(data.amount) || 0
                    const invoiceVAT = parseFloat(data.vat) || 0
                    const invoiceTP = invoiceAmount - invoiceVAT
                    
                    // Calculate total subtotal for all products (for proportional distribution)
                    let totalSubtotal = 0
                    productDetails.forEach(item => {
                        const qty = parseInt(item.quantity) || 0
                        const cpu = parseFloat(item.cost_per_unit) || 0
                        totalSubtotal += qty * cpu
                    })
                    
                    const products = productDetails.map((item, index) => {
                        // Reconstruct sizeOrMedType and sizeOrMedValue from saved data
                        // New logic: If dose exists → Size type, otherwise Med/Strip type
                        const hasDose = item.dose && item.dose.trim() !== ''
                        const reconstructedSizeOrMedType = hasDose ? 'Size' : 'Med/Strip'
                        const reconstructedSizeOrMedValue = hasDose ? (item.dose || '') : ''
                        const medPerStripVal = parseInt(item.medicine_per_strips || item.medicine_per_strip) || 1
                        
                        const qty = parseInt(item.quantity) || 0
                        const cpu = parseFloat(item.cost_per_unit) || 0
                        const subtotal = qty * cpu
                        
                        // Calculate this product's share of TP and VAT proportionally
                        // based on its subtotal relative to total subtotal
                        const ratio = totalSubtotal > 0 ? subtotal / totalSubtotal : 0
                        const productTP = invoiceTP * ratio
                        const productVAT = invoiceVAT * ratio
                        
                        return {
                            id: item.product_id,
                            productName: item.product_name,
                            genericName: item.generic_name || '',
                            strength: item.strength || '',
                            dose: item.dose || '',
                            category: item.category || '',
                            barcode: item.barcode || '',
                            batchNumber: item.batch_number,
                            expiryDate: normalizeToDateInput(item.expiry_date),
                            quantity: qty,
                            bonusQuantity: parseInt(item.bonus_quantity) || 0,
                            sizeOrMedType: reconstructedSizeOrMedType,
                            sizeOrMedValue: reconstructedSizeOrMedValue,
                            medPerStrip: medPerStripVal,
                            totalTP: productTP,
                            totalVAT: productVAT,
                            costPerUnit: cpu,
                            vatPercentage: parseFloat(item.vat || item.vat_percentage) || 0,
                            ratePerUnit: parseFloat(item.rate_per_unit) || 0,
                            productType: item.product_type || item.type || 'Pharmaceutical',
                            prescriptionRequired: item.prescription_required || item.is_prescription_required || 'Yes',
                            origin: item.origin || 'Local',
                            subtotal: subtotal,
                            supplierName: item.supplier_name || supplierName
                        }
                    })
                    setProductsInTable(products)
                }

                // Populate Section 4 - Invoice Summary from backend response
                // Use the values from backend instead of recalculating from products
                const invoiceAmount = parseFloat(data.amount) || 0
                const vatAmount = parseFloat(data.vat) || 0
                const tpAmount = invoiceAmount - vatAmount // Total TP = Invoice Value - VAT
                
                setInvoiceValue(invoiceAmount)
                setTotalTPValue(tpAmount)
                setTotalVATValue(vatAmount)
                setNetValue(parseFloat(data.total_amount) || 0)
                setTradeDiscountPercentage(parseFloat(data.trade_discount_percentage) || 0)
                setTradeDiscountAmount(parseFloat(data.trade_discount_amount) || 0)
                setPaymentMode(data.payment_mode || 'CASH_IN_HAND')
                // Set bank if payment mode is Cash in account
                if (data.bank_id && data.bank_name) {
                    setSelectedBank({
                        value: data.bank_id,
                        label: data.bank_name,
                        id: data.bank_id,
                        bank_name: data.bank_name
                    })
                }
                
                // Reset the skip flag after all state updates have been processed
                // Use setTimeout to ensure all useEffects have had a chance to skip
                setTimeout(() => {
                    skipNextRecalculationRef.current = false
                }, 100)
                
                // After loading from backend, check if there's a draft with unsaved changes
                // The draft has priority because it contains more recent user edits
                const editDraftKey = `${DRAFT_KEYS.PRODUCT_RECEIVING}_edit_${id}`
                const draft = loadDraft(editDraftKey)
                if (draft) {
                    // Restore draft data (overwrites backend data with user's unsaved changes)
                    restoreDraftData(draft)
                }
                
                // Mark initial load as complete so draft saving can begin
                setIsInitialLoadComplete(true)
            }
        } catch (error) {            alert('Failed to load invoice data: ' + (error.message || error))
            setIsInitialLoadComplete(true) // Allow saving even if load fails
        } finally {
            setLoadingInvoice(false)
        }
    }

    const handleVendorChange = async (selectedOption, actionMeta) => {
        if (actionMeta.action === 'select-option') {
            // Existing vendor selected - just update vendor info
            setSelectedVendor(selectedOption)
            setVendorContact(selectedOption.contact || '')
            setVendorCountryCode(selectedOption.country_code || '+880')
            setVendorAddress(selectedOption.address || '')
        } else if (actionMeta.action === 'create-option') {
            // New vendor typed - mark as new, will be created when form is submitted
            const newVendorOption = {
                value: null, // No ID yet - will be created later
                label: selectedOption.label,
                displayName: selectedOption.label,
                vendor_id: '', // Will be generated by backend
                contact: '',
                address: '',
                country_code: '+880',
                __isNew__: true // Mark as new - will be created when needed
            }
            setSelectedVendor(newVendorOption)
            setVendorContact('')
            setVendorCountryCode('+880')
            setVendorAddress('')
        } else if (actionMeta.action === 'clear') {
            // Vendor cleared
            setSelectedVendor(null)
            setVendorContact('')
            setVendorCountryCode('+880')
            setVendorAddress('')
            loadSuppliers()
        }
    }

    const handleSupplierChange = async (selectedOption, actionMeta) => {
        if (actionMeta.action === 'select-option') {
            // Existing supplier selected
            setSelectedSupplier(selectedOption)
            setSupplierContact(selectedOption.contact || '')
            setSupplierCountryCode(selectedOption.country_code || '+880')
            setSupplierAddress(selectedOption.address || '')
        } else if (actionMeta.action === 'create-option') {
            // New supplier typed - mark as new, will be created when form is submitted
            const newSupplierOption = {
                value: null, // No ID yet - will be created later
                label: selectedOption.label,
                displayName: selectedOption.label,
                supplier_id: '', // Will be generated by backend
                contact: '',
                address: '',
                country_code: '+880',
                is_approved: false,
                __isNew__: true // Mark as new - will be created when needed
            }
            setSelectedSupplier(newSupplierOption)
            setSupplierContact('')
            setSupplierCountryCode('+880')
            setSupplierAddress('')
        } else if (actionMeta.action === 'clear') {
            // Supplier cleared
            setSelectedSupplier(null)
            setSupplierContact('')
            setSupplierCountryCode('+880')
            setSupplierAddress('')
        }
    }

    const handleProductSelect = (selectedOption) => {
        if (!selectedOption) {
            // Clear all product detail fields when product is cleared
            setSelectedProduct(null)
            setProductId('')
            setProductName('')
            setGenericName('')
            setStrength('')
            setDose('')
            setCategory('')
            setBarcode('')
            setSizeOrMedValue('')
            setSizeOrMedType('Size')
            setBatchNumber('')
            setExpiryDate('')
            setQuantity('')
            setBonusQuantity('')
            setTotalTP('')
            setTotalVAT('')
            setCostPerUnit('')
            setRatePerUnit('')
            
            // Also clear supplier fields to allow showing all products from all suppliers
            setSelectedSupplier(null)
            setSupplierContact('')
            setSupplierCountryCode('+880')
            setSupplierAddress('')
            
            setProductOptions([])
            setProductPage(0)
            setHasMoreProducts(true)
            return
        }        
        // If value or label is a Promise, it means something went wrong with input handling
        if (selectedOption.value instanceof Promise || selectedOption.label instanceof Promise) {            alert('Error: Invalid product selection. Please refresh the page and try again.')
            return
        }
        
        // Find the exact option from the options array to ensure reference match
        const matchingOption = productOptions.find(opt => opt.value === String(selectedOption.value))
        
        let optionToUse
        if (matchingOption) {
            // Use the option from the list
            optionToUse = matchingOption        } else if (selectedOption.__isNew__) {
            // New product being created - ensure clean string values
            optionToUse = {
                value: String(selectedOption.value || selectedOption.label || ''),
                label: String(selectedOption.label || ''),
                __isNew__: true
            }        } else {
            // Fallback - use the selected option
            optionToUse = selectedOption        }
        
        // Store the option for display - use the exact object from options array
        setSelectedProduct(optionToUse)        
        // If option is not in the list and not a new product, add it
        if (!matchingOption && !selectedOption.__isNew__) {
            setProductOptions([optionToUse, ...productOptions])
        }
        
        setProductId(String(optionToUse.value || ''))
        setProductName(String(optionToUse.label || ''))
        setGenericName(String(optionToUse.genericName || ''))
        setStrength(String(optionToUse.strength || ''))
        setDose(String(optionToUse.dose || ''))
        setCategory(String(optionToUse.category || ''))
        setBarcode(String(optionToUse.barcode || ''))
        
        // Auto-populate cost and rate if available from database
        if (optionToUse.costPerUnit) {
            setCostPerUnit(parseFloat(optionToUse.costPerUnit) || '')
        }
        if (optionToUse.ratePerUnit) {
            setRatePerUnit(parseFloat(optionToUse.ratePerUnit) || '')
        }
        // Auto-populate size or determine type based on saved data
        // New logic: Check if dose exists → Size type, otherwise Med/Strip type
        if (optionToUse.dose) {
            // If dose exists, it's Size type
            setSizeOrMedValue(String(optionToUse.dose || ''))
            setSizeOrMedType('Size')
        } else {
            // No dose means Med/Strip type (size input will be hidden)
            setSizeOrMedValue('')
            setSizeOrMedType('Med/Strip')
        }
        
        // Auto-fetch and populate supplier for this product (async operation but don't block UI)
        if (optionToUse.supplier_id && !selectedSupplier) {
            // Find supplier from options
            const supplierOption = supplierOptions.find(s => s.value === optionToUse.supplier_id)
            
            if (supplierOption) {
                // Supplier already in options, just select it
                setSelectedSupplier(supplierOption)
                setSupplierContact(supplierOption.contact || '')
                setSupplierCountryCode(supplierOption.country_code || '+880')
                setSupplierAddress(supplierOption.address || '')
            } else if (optionToUse.supplier_name) {
                // Supplier not in options, create temporary option
                const tempSupplierOption = {
                    value: optionToUse.supplier_id,
                    label: optionToUse.supplier_name,
                    supplier_id: optionToUse.supplier_id || '',
                    __isTemp__: true
                }
                setSelectedSupplier(tempSupplierOption)
                
                // Try to fetch full supplier details in background
                getSuppliersHelper()
                    .then(suppliers => {
                        const fullSuppliers = suppliers?.data?.suppliers || suppliers || []
                        const fullSupplier = fullSuppliers.find(s => String(s.id) === optionToUse.supplier_id)
                        
                        if (fullSupplier) {
                            const completeSupplierOption = {
                                value: String(fullSupplier.id),
                                label: String(fullSupplier.name),
                                displayName: String(fullSupplier.name),
                                contact: String(fullSupplier.mobile_no || fullSupplier.contact || ''),
                                address: String(fullSupplier.address || ''),
                                country_code: String(fullSupplier.country_code || '+880'),
                                is_approved: fullSupplier.is_approved,
                                supplier_id: String(fullSupplier.supplier_id || '')
                            }
                            setSelectedSupplier(completeSupplierOption)
                            setSupplierContact(completeSupplierOption.contact)
                            setSupplierCountryCode(completeSupplierOption.country_code)
                            setSupplierAddress(completeSupplierOption.address)                        }
                    })
                    .catch(error => {                    })
            }
        }
    }

    const handleBarcodeKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleBarcodeLookup()
        }
    }

    const handleBarcodeLookup = async () => {
        if (!barcode || !barcode.trim()) {
            return
        }

        try {
            const response = await getProductsHelper({ barcode: barcode.trim() })
            const products = response?.products || response || []
            const productData = Array.isArray(products) && products.length > 0 ? products[0] : null

            if (!productData) {
                alert(`Product not found for barcode: ${barcode}.`)
                return
            }

            const productId = productData.id
            setProductId(String(productId))

            const productOption = {
                value: String(productId),
                label: productData.name || '',
                genericName: String(productData.generic_name || ''),
                strength: String(productData.strength || ''),
                dose: String(productData.dose || ''),
                category: typeof productData.category === 'string' ? productData.category : (productData.category?.label || productData.category?.value || ''),
                barcode: String(productData.barcode || barcode),
                costPerUnit: String(productData.cost_per_unit || ''),
                ratePerUnit: String(productData.rate_per_unit || ''),
                medPerStrip: String(productData.medicine_per_strip || productData.medicine_per_strips || ''),
                supplier_id: String(productData.supplier_id || ''),
                supplier_name: String(productData.supplier_name || '')
            }
            setSelectedProduct(productOption)

            setProductOptions(prevOptions => {
                const exists = prevOptions.some(opt => opt.value === productOption.value)
                if (!exists) {
                    return [productOption, ...prevOptions]
                }
                return prevOptions
            })

            // Populate supplier from product data
            if (productData.supplier_id) {
                const supplierId = String(productData.supplier_id)
                const allSuppliersData = await getSuppliersHelper()
                const suppliers = allSuppliersData?.data?.suppliers || allSuppliersData || []

                if (Array.isArray(suppliers)) {
                    const foundSupplier = suppliers.find(s => String(s.id) === supplierId)

                    if (foundSupplier) {
                        const supplierOption = {
                            value: String(foundSupplier.id),
                            label: String(foundSupplier.name),
                            displayName: String(foundSupplier.name),
                            contact: String(foundSupplier.mobile_no || foundSupplier.contact || ''),
                            address: String(foundSupplier.address || ''),
                            country_code: String(foundSupplier.country_code || '+880'),
                            is_approved: foundSupplier.is_approved,
                            supplier_id: String(foundSupplier.supplier_id || '')
                        }
                        setSelectedSupplier(supplierOption)
                        setSupplierContact(supplierOption.contact)
                        setSupplierCountryCode(supplierOption.country_code)
                        setSupplierAddress(supplierOption.address)

                        const options = suppliers
                            .filter(supplier => supplier && (supplier.id || supplier.id === 0) && supplier.name)
                            .map((supplier) => ({
                                value: String(supplier.id),
                                label: String(supplier.name),
                                displayName: String(supplier.name),
                                contact: String(supplier.mobile_no || supplier.contact || ''),
                                address: String(supplier.address || ''),
                                country_code: String(supplier.country_code || '+880'),
                                is_approved: supplier.is_approved,
                                supplier_id: String(supplier.supplier_id || '')
                            }))
                        setSupplierOptions(options)
                        setAllSuppliers(suppliers)
                    }
                }
            }

            // Populate all product fields
            setProductName(productData.name || '')
            setGenericName(productData.generic_name || '')
            setStrength(productData.strength || '')
            setDose(productData.dose || '')
            setCategory(productData.category || '')
            setBarcode(productData.barcode || barcode)

            if (productData.type) {
                const productType = productData.type.toUpperCase()
                if (productType === 'PHARMACEUTICAL') {
                    setProductType('Pharmaceutical')
                } else if (productType === 'NON_PHARMACEUTICAL') {
                    setProductType('Non Pharmaceutical')
                }
            }
            if (productData.origin) {
                const origin = productData.origin.toUpperCase()
                if (origin === 'LOCAL') {
                    setOrigin('Local')
                } else if (origin === 'FOREIGN') {
                    setOrigin('Foreign')
                } else {
                    setOrigin(productData.origin)
                }
            }

            if (productData.cost_per_unit) {
                setCostPerUnit(parseFloat(productData.cost_per_unit))
            }
            if (productData.rate_per_unit) {
                setRatePerUnit(parseFloat(productData.rate_per_unit))
            }
            if (productData.medicine_per_strips || productData.medicine_per_strip) {
                setSizeOrMedValue(String(productData.medicine_per_strips || productData.medicine_per_strip))
                setSizeOrMedType('Med/Strip')
            }
        } catch (error) {
            alert(`Product not found for barcode: ${barcode}. ${error.message || error}`)
        }
    }

    const handleGenerateBarcode = () => {
        // Generate barcode: timestamp + random integer (no decimals)
        const generatedBarcode = generateProductBarcode()
        setBarcode(generatedBarcode)
        // Auto-open drawer to view the newly generated barcode
        setBarcodeDrawerOpen(true)
    }

    const handleViewBarcode = () => {
        if (barcode) {
            setBarcodeDrawerOpen(true)
        } else {
            alert('No barcode available. Please generate a barcode first.')
        }
    }

    const handleGenerateNewBarcode = () => {
        // Generate a new barcode
        const generatedBarcode = generateProductBarcode()
        setBarcode(generatedBarcode)
        // Optionally open drawer to show new barcode
        setBarcodeDrawerOpen(true)
    }

    const handleRemoveBarcode = () => {
        setBarcode('')
        // Close drawer if open
        if (barcodeDrawerOpen) {
            setBarcodeDrawerOpen(false)
        }
    }

    const handleBarcodeDrawerToggle = () => {
        setBarcodeDrawerOpen(!barcodeDrawerOpen)
    }

    const handleDownloadBarcode = () => {
        if (svgRef.current) {
            const svg = svgRef.current.innerHTML
            const blob = new Blob([svg], { type: 'image/svg+xml' })
            const objectUrl = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = objectUrl
            link.download = `barcode-${barcode}.svg`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            setTimeout(() => URL.revokeObjectURL(objectUrl), 5000)
        }
    }

    const clearProductForm = () => {
        setSelectedProduct(null)
        setProductId('')
        setProductName('')
        setGenericName('')
        setStrength('')
        setDose('')
        setCategory('')
        setBarcode('')
        setBatchNumber('')
        setExpiryDate('')
        setQuantity('')
        setBonusQuantity('')
        setSizeOrMedType('Size')
        setSizeOrMedValue('')
        setTotalTP('')
        setTotalVAT('')
        setCostPerUnit('')
        setRatePerUnit('')
        setProductType('Pharmaceutical')
        setPrescriptionRequired('Yes')
        setPrescriptionDetails('')
        setOrigin('Local')
        setSelectedTableRowIndex(null)
        
        setProductOptions([])
        setProductPage(0)
        setHasMoreProducts(true)
    }

    const handleAddProduct = async () => {
        // Validation
        if (!productName) {
            alert('Product name is required')
            return
        }
        // Validate Size field when Size type is selected
        if (sizeOrMedType === 'Size' && (!sizeOrMedValue || sizeOrMedValue.trim() === '')) {
            alert('Size is required when Size type is selected')
            return
        }
        if (!batchNumber) {
            alert('Batch number is required')
            return
        }
        if (!expiryDate) {
            alert('Expiry date is required')
            return
        }
        if (!quantity || quantity <= 0) {
            alert('Quantity must be greater than 0')
            return
        }
        if (!costPerUnit || costPerUnit <= 0) {
            alert('Cost per unit (CPU) must be greater than 0')
            return
        }
        if (!ratePerUnit || ratePerUnit <= 0) {
            alert('Rate per unit (RPU) must be greater than 0')
            return
        }
        
        // Requirement #4: RPU can't be less than or equal to CPU
        if (parseFloat(ratePerUnit) <= parseFloat(costPerUnit)) {
            alert('⚠️ RPU cannot be less than or equal to CPU!\n\nRate Per Unit (RPU) must be greater than Cost Per Unit (CPU).\n\nCurrent CPU: ' + costPerUnit + '\nCurrent RPU: ' + ratePerUnit + '\n\nPlease increase the RPU to proceed.')
            return
        }

        // Check expiry date is in future
        const expiryDateTime = new Date(expiryDate)
        if (expiryDateTime <= new Date()) {
            alert('Expiry date must be in the future')
            return
        }

        // Check for duplicate batch number (only for new products, not when editing)
        if (selectedTableRowIndex === null) {
            const duplicateBatch = productsInTable.find(
                (p) => p.batchNumber === batchNumber && p.id === (selectedProduct?.value || null)
            )
            if (duplicateBatch) {
                alert('Batch number already exists for this product')
                return
            }
        }

        const totalQty = parseInt(quantity) + parseInt(bonusQuantity || 0)
        const subtotal = parseInt(quantity) * parseFloat(costPerUnit)

        // Calculate VAT percentage from Total TP and Total VAT
        const tpValue = parseFloat(totalTP || 0)
        const vatValue = parseFloat(totalVAT || 0)
        const vatPercentageValue = tpValue > 0 ? (vatValue / tpValue) * 100 : 0

        // Extract dose and medPerStrip values based on sizeOrMedType
        // If Size is selected → save size to dose field, medicine_per_strips = 1
        // If Med/Strip is selected → dose is empty, medicine_per_strips = 1
        const effectiveDoseValue = sizeOrMedType === 'Size' ? (sizeOrMedValue || '') : ''
        const medPerStripValue = 1 // Always 1 as per new requirement

        // Determine product ID - use existing ID or mark as new
        const productIdValue = selectedProduct?.value
        const isNumericId = productIdValue && !isNaN(productIdValue) && typeof parseInt(productIdValue) === 'number'
        let finalProductId = isNumericId ? productIdValue : null
        
        // For new products, validate required fields but don't create yet
        // Products will be created when invoice is saved/held
        if (!finalProductId && selectedProduct?.__isNew__) {
            if (!category) {
                alert('Category is required for new products')
                return
            }
            const finalDose = effectiveDoseValue || strength
            if (!finalDose) {
                alert('Either Size/Dose or Strength is required for new products')
                return
            }
            if (!genericName) {
                alert('Generic name is required for new products')
                return
            }
            if (!selectedSupplier) {
                alert('Supplier is required for new products')
                return
            }
        }
        
        const productData = {
            id: finalProductId, // Numeric ID for existing products, null for new products
            productId,
            productName,
            genericName,
            strength,
            dose: effectiveDoseValue, // Use Size value when Size type is selected
            category,
            barcode,
            batchNumber,
            expiryDate,
            quantity: parseInt(quantity),
            bonusQuantity: parseInt(bonusQuantity || 0),
            sizeOrMedType,
            sizeOrMedValue,
            medPerStrip: medPerStripValue, // Use Med/Strip value when Med/Strip type is selected
            totalTP: parseFloat(totalTP || 0),
            totalVAT: parseFloat(totalVAT || 0),
            vatPercentage: parseFloat(vatPercentageValue.toFixed(2)),
            costPerUnit: parseFloat(costPerUnit),
            ratePerUnit: parseFloat(ratePerUnit),
            productType,
            prescriptionRequired,
            prescriptionDetails,
            origin,
            subtotal,
            supplierName: selectedSupplier?.label || '',
            __isNew__: selectedProduct?.__isNew__ || false // Mark if this is a new product to be created
        }

        if (selectedTableRowIndex !== null) {
            // Update existing product in table
            const updatedProducts = [...productsInTable]
            updatedProducts[selectedTableRowIndex] = productData
            setProductsInTable(updatedProducts)
            clearProductForm()
        } else {
            // Add new product to table - keep form fields populated
            setProductsInTable([...productsInTable, productData])
            setSelectedTableRowIndex(null)
        }
    }

    const handleSaveProduct = () => {
        // Save button should just clear the form without validation
        clearProductForm()
    }

    const handleDeleteProduct = () => {
        if (selectedTableRowIndex === null) {
            alert('Please select a product from the table to delete')
            return
        }

        const updatedProducts = productsInTable.filter((_, index) => index !== selectedTableRowIndex)
        setProductsInTable(updatedProducts)
        clearProductForm()
    }

    // Requirement #11: Double-click to edit product from table
    const handleTableRowDoubleClick = (index) => {
        setSelectedTableRowIndex(index)
        const product = productsInTable[index]

        // Populate Section 2 with selected product data
        setSelectedProduct(product.id ? { value: product.id, label: product.productName } : null)
        setProductId(product.productId || '')
        setProductName(product.productName)
        setGenericName(product.genericName)
        setStrength(product.strength)
        setDose(product.dose)
        setCategory(product.category)
        setBarcode(product.barcode)
        setBatchNumber(product.batchNumber)
        setExpiryDate(normalizeToDateInput(product.expiryDate))
        setQuantity(product.quantity)
        setBonusQuantity(product.bonusQuantity)
        // Use dose or strength as fallback for sizeOrMedValue
        const dblClickSizeOrMedValue = product.sizeOrMedValue || product.dose || product.strength || ''
        setSizeOrMedValue(dblClickSizeOrMedValue)
        // Default to 'Med/Strip' if no size value to avoid validation error
        const dblClickHasSizeValue = dblClickSizeOrMedValue && dblClickSizeOrMedValue.toString().trim() !== ''
        setSizeOrMedType(dblClickHasSizeValue ? (product.sizeOrMedType || 'Med/Strip') : 'Med/Strip')
        setTotalTP(product.totalTP)
        setTotalVAT(product.totalVAT)
        // Don't set costPerUnit here - let it be recalculated by useEffect
        // setCostPerUnit(product.costPerUnit)
        setRatePerUnit(product.ratePerUnit)
        setProductType(product.productType)
        setPrescriptionRequired(product.prescriptionRequired)
        setPrescriptionDetails(product.prescriptionDetails || '')
        setOrigin(product.origin)
    }

    // Single click highlights the row AND populates the form for editing
    const handleTableRowClick = (index) => {
        setSelectedTableRowIndex(index)
        const product = productsInTable[index]

        // Populate Section 2 with selected product data (same as double-click)
        setSelectedProduct(product.id ? { value: product.id, label: product.productName } : null)
        setProductId(product.productId || '')
        setProductName(product.productName)
        setGenericName(product.genericName)
        setStrength(product.strength)
        setDose(product.dose)
        setCategory(product.category)
        setBarcode(product.barcode)
        setBatchNumber(product.batchNumber)
        setExpiryDate(normalizeToDateInput(product.expiryDate))
        setQuantity(product.quantity)
        setBonusQuantity(product.bonusQuantity)
        // Use dose or strength as fallback for sizeOrMedValue
        const clickSizeOrMedValue = product.sizeOrMedValue || product.dose || product.strength || ''
        setSizeOrMedValue(clickSizeOrMedValue)
        // Default to 'Med/Strip' if no size value to avoid validation error
        const clickHasSizeValue = clickSizeOrMedValue && clickSizeOrMedValue.toString().trim() !== ''
        setSizeOrMedType(clickHasSizeValue ? (product.sizeOrMedType || 'Med/Strip') : 'Med/Strip')
        setTotalTP(product.totalTP)
        setTotalVAT(product.totalVAT)
        setCostPerUnit(product.costPerUnit)
        setRatePerUnit(product.ratePerUnit)
        setProductType(product.productType)
        setPrescriptionRequired(product.prescriptionRequired)
        setPrescriptionDetails(product.prescriptionDetails || '')
        setOrigin(product.origin)
    }

    const calculateInvoiceTotals = (recalculateDiscount = true) => {
        let tpTotal = 0
        let vatTotal = 0

        productsInTable.forEach((product) => {
            tpTotal += product.totalTP
            vatTotal += product.totalVAT
        })

        // Invoice Value = TP + VAT
        const invoiceTotal = tpTotal + vatTotal
        setInvoiceValue(invoiceTotal)
        setTotalTPValue(tpTotal)
        setTotalVATValue(vatTotal)

        // Requirement #10: Trade discount will be counted on Total TP
        // Only recalculate discount when products change, not when discount fields change
        if (recalculateDiscount && tradeDiscountPercentage > 0) {
            const discountAmount = (tpTotal * tradeDiscountPercentage) / 100
            setTradeDiscountAmount(discountAmount)
        }

        // Net Value = Invoice Value - Trade Discount
        const netAmount = invoiceTotal - tradeDiscountAmount
        setNetValue(netAmount)
    }

    const handleTradeDiscountPercentageChange = (value) => {
        const percentage = parseFloat(value) || 0
        setTradeDiscountPercentage(percentage)

        // Requirement #10: Trade discount calculated on Total TP
        if (percentage > 0 && totalTPValue > 0) {
            const amount = (totalTPValue * percentage) / 100
            setTradeDiscountAmount(amount)
        } else if (percentage === 0) {
            setTradeDiscountAmount(0)
        }
    }

    const handleTradeDiscountAmountChange = (value) => {
        const amount = parseFloat(value) || 0
        setTradeDiscountAmount(amount)

        // Requirement #10: Trade discount percentage calculated based on Total TP
        if (amount > 0 && totalTPValue > 0) {
            const percentage = (amount / totalTPValue) * 100
            setTradeDiscountPercentage(percentage)
        } else if (amount === 0) {
            setTradeDiscountPercentage(0)
        }
    }

    const validateForm = () => {
        if (!invoiceNumber) {
            alert('Invoice number is required')
            return false
        }
        if (!selectedSupplier) {
            alert('Supplier is required')
            return false
        }
        if (productsInTable.length === 0) {
            alert('Please add at least one product')
            return false
        }
        return true
    }

    const prepareSubmitData = (status, updatedProducts = null, updatedSupplierId = null) => {
        const billDetail = {
            [BILL_DETAILS_FIELDS.INVOICE_NUMBER]: invoiceNumber,
            [BILL_DETAILS_FIELDS.DATE]: invoiceDate,
            [BILL_DETAILS_FIELDS.VENDOR_ID]: selectedVendor?.value || null, // Numeric ID
            [BILL_DETAILS_FIELDS.VENDOR_CONTACT]: vendorContact,
            [BILL_DETAILS_FIELDS.VENDOR_COUNTRY_CODE]: vendorCountryCode,
            [BILL_DETAILS_FIELDS.VENDOR_ADDRESS]: vendorAddress,
            [BILL_DETAILS_FIELDS.SUPPLIER_ID]: updatedSupplierId || selectedSupplier?.value || null,
            [BILL_DETAILS_FIELDS.SUPPLIER_PHONE]: supplierContact,
            [BILL_DETAILS_FIELDS.SUPPLIER_COUNTRY_CODE]: supplierCountryCode,
            [BILL_DETAILS_FIELDS.SUPPLIER_ADDRESS]: supplierAddress,
            [BILL_DETAILS_FIELDS.NOTES]: notes,
            status: status
        }

        // Use updated products if provided (from createNewProducts), otherwise use state
        const productsToSubmit = updatedProducts || productsInTable
        const productDetails = productsToSubmit.map((product) => {
            // All products should have numeric IDs at this point (created in backend if new)
            const productId = product.id            
            // Validation: Ensure we have a valid product ID
            if (!productId || isNaN(productId)) {
                throw new Error(`Invalid product ID for "${product.productName}". Product must be created before submitting invoice.`)
            }
            
            return {
                id: productId,
                [PRODUCT_DETAIL_FIELDS.PRODUCT_NAME]: product.productName,
                [PRODUCT_DETAIL_FIELDS.GENERIC_NAME]: product.genericName,
                [PRODUCT_DETAIL_FIELDS.STRENGTH]: product.strength,
                [PRODUCT_DETAIL_FIELDS.DOSE]: product.dose,
                [PRODUCT_DETAIL_FIELDS.PRODUCT_CATEGORY]: product.category,
                [PRODUCT_DETAIL_FIELDS.PRODUCT_BARCODE]: product.barcode,
                [PRODUCT_DETAIL_FIELDS.BATCH_NUMBER]: product.batchNumber,
                [PRODUCT_DETAIL_FIELDS.EXPIRY_DATE]: product.expiryDate,
                [PRODUCT_DETAIL_FIELDS.QUANTITY]: product.quantity,
                [PRODUCT_DETAIL_FIELDS.BONUS_QUANITY]: product.bonusQuantity,
                [PRODUCT_DETAIL_FIELDS.MED_PER_STRIP]: product.medPerStrip,
                [PRODUCT_DETAIL_FIELDS.TOTAL_TP]: product.totalTP,
                [PRODUCT_DETAIL_FIELDS.TOTAL_VAT]: product.totalVAT,
                [PRODUCT_DETAIL_FIELDS.PRODUCT_COST]: product.costPerUnit,
                [PRODUCT_DETAIL_FIELDS.VAT_PERCENTAGE]: product.vatPercentage,
                [PRODUCT_DETAIL_FIELDS.PRODUCT_NEW_COST]: product.costPerUnit,
                [PRODUCT_DETAIL_FIELDS.RATE_PER_UNIT]: product.ratePerUnit,
                [PRODUCT_DETAIL_FIELDS.MEDICAL_CATEGORY]: product.productType,
                [PRODUCT_DETAIL_FIELDS.PRESCRIPTION_REQUIRED]: product.prescriptionRequired,
                [PRODUCT_DETAIL_FIELDS.ORIGIN]: product.origin
            }
        })

        const paymentDetail = {
            [PAYMENT_DETAILS_FIELDS.AMOUNT]: invoiceValue,
            [PAYMENT_DETAILS_FIELDS.VAT]: totalVATValue,
            [PAYMENT_DETAILS_FIELDS.TRADE_DISCOUNT_PERCENTAGE]: tradeDiscountPercentage,
            [PAYMENT_DETAILS_FIELDS.TRADE_DISCOUNT_AMOUNT]: tradeDiscountAmount,
            [PAYMENT_DETAILS_FIELDS.TOTAL_AMOUNT]: netValue,
            [PAYMENT_DETAILS_FIELDS.PAYMENT_MODE]: paymentMode,
            [PAYMENT_DETAILS_FIELDS.BANK_ID]: paymentMode === 'CASH_IN_ACCOUNT' && selectedBank ? selectedBank.id : null,
            [PAYMENT_DETAILS_FIELDS.BANK_NAME]: paymentMode === 'CASH_IN_ACCOUNT' && selectedBank ? selectedBank.bank_name : null
        }

        const data = {
            billDetail: billDetail,
            productDetails: productDetails,
            paymentDetails: paymentDetail
        }

        // Include product_receiving_id at top level for edit mode
        if (isEditMode && productReceivingId) {
            data.product_receiving_id = productReceivingId
        }
        
        // Include order_book_id when coming from Order Book
        if (orderBookId) {
            data.order_book_id = orderBookId
        }

        return data
    }

    const handleAdd = async () => {
        if (!validateForm()) return

        try {
            setLoading(true)
            
            // 1. Create or update vendor first
            const updatedVendor = await updateVendorDetails()
            const vendorId = updatedVendor?.value || selectedVendor?.value
            
            // 2. Create or update supplier (pass vendor ID)
            const updatedSupplier = await updateSupplierDetails(vendorId)
            const supplierId = updatedSupplier?.value || selectedSupplier?.value
            
            // 3. Create new products (pass supplier ID) - returns updated products array
            let updatedProducts = productsInTable
            if (supplierId) {
                updatedProducts = await createNewProducts(supplierId)
            }
            
            // Pass updated products and supplier ID to avoid React state timing issues
            const data = prepareSubmitData('RECEIVED', updatedProducts, supplierId)
            await addProductReceivingHelper(data)

            // If no error thrown, it was successful
            dispatch(addProductReceivingAlertAction(true))
            setTimeout(() => {
                dispatch(removeProductReceivingAlertAction())
            }, 5000)
            navigate('/home/details')
        } catch (error) {            alert(error.message || error || 'Failed to save product receiving')
        } finally {
            setLoading(false)
        }
    }

    const handleHold = async () => {
        setShowHoldConfirmation(true)
    }

    const updateVendorDetails = async () => {
        // Create new vendor if marked as __isNew__
        if (selectedVendor?.__isNew__ && !selectedVendor?.value) {
            try {
                let fullContact = vendorContact || null
                if (fullContact && vendorCountryCode && !fullContact.startsWith(vendorCountryCode)) {
                    fullContact = `${vendorCountryCode}${fullContact}`
                }
                
                const newVendorData = {
                    name: selectedVendor.label,
                    contact: fullContact,
                    address: vendorAddress || null
                }
                
                const createdVendor = await addVendorHelper(newVendorData)
                
                // Update selectedVendor with backend response
                const updatedVendorOption = {
                    value: String(createdVendor.id),
                    label: createdVendor.name,
                    displayName: createdVendor.name,
                    vendor_id: String(createdVendor.vendor_id || ''),
                    contact: String(createdVendor.contact || ''),
                    address: String(createdVendor.address || ''),
                    country_code: String(createdVendor.country_code || '+880'),
                    __isNew__: false
                }
                setSelectedVendor(updatedVendorOption)
                setAllVendors([...allVendors, createdVendor])
                setVendorOptions([...vendorOptions, updatedVendorOption])
                
                return updatedVendorOption
            } catch (error) {
                throw new Error('Failed to create vendor: ' + (error.message || error))
            }
        }
        // Update existing vendor details if vendor is selected and has contact/address info
        else if (selectedVendor && selectedVendor.value && (vendorContact || vendorAddress)) {
            try {
                let fullContact = vendorContact || null
                if (fullContact && vendorCountryCode && !fullContact.startsWith(vendorCountryCode)) {
                    fullContact = `${vendorCountryCode}${fullContact}`
                }
                
                const updateData = {
                    id: selectedVendor.value,
                    name: selectedVendor.label,
                    contact: fullContact,
                    address: vendorAddress || null
                }
                await updateVendorHelper(updateData)
                await loadVendors()
            } catch (error) {
                // Don't fail the whole operation if vendor update fails
            }
        }
        return selectedVendor
    }

    const updateSupplierDetails = async (vendorId = null) => {
        // Create new supplier if marked as __isNew__
        if (selectedSupplier?.__isNew__ && !selectedSupplier?.value) {
            try {
                let fullContact = supplierContact || null
                if (fullContact && supplierCountryCode && !fullContact.startsWith(supplierCountryCode)) {
                    fullContact = `${supplierCountryCode}${fullContact}`
                }
                
                // Only link vendor to supplier if vendor is explicitly selected
                const explicitVendorId = vendorId || (selectedVendor?.value ? selectedVendor.value : null)
                
                const newSupplierData = {
                    name: selectedSupplier.label,
                    mobile_no: fullContact,
                    address: supplierAddress || null,
                    is_approved: false,
                    vendor: explicitVendorId
                }
                
                const createdSupplier = await addSuppliersHelper(newSupplierData)
                
                // Update selectedSupplier with backend response
                const updatedSupplierOption = {
                    value: String(createdSupplier.id),
                    label: createdSupplier.name,
                    displayName: createdSupplier.name,
                    supplier_id: String(createdSupplier.supplier_id || ''),
                    contact: String(createdSupplier.mobile_no || ''),
                    address: String(createdSupplier.address || ''),
                    country_code: String(createdSupplier.country_code || '+880'),
                    is_approved: createdSupplier.is_approved,
                    __isNew__: false
                }
                setSelectedSupplier(updatedSupplierOption)
                setAllSuppliers([...allSuppliers, createdSupplier])
                setSupplierOptions([...supplierOptions, updatedSupplierOption])
                
                return updatedSupplierOption
            } catch (error) {
                throw new Error('Failed to create supplier: ' + (error.message || error))
            }
        }
        // Update existing supplier details if supplier is selected and has contact/address info
        else if (selectedSupplier && selectedSupplier.value && (supplierContact || supplierAddress)) {
            try {
                let fullContact = supplierContact || null
                if (fullContact && supplierCountryCode && !fullContact.startsWith(supplierCountryCode)) {
                    fullContact = `${supplierCountryCode}${fullContact}`
                }
                
                const updateData = {
                    id: selectedSupplier.value,
                    name: selectedSupplier.label,
                    mobile_no: fullContact,
                    address: supplierAddress || null,
                    is_approved: selectedSupplier.is_approved || false
                    // Note: Don't update vendor field for existing suppliers during invoice save
                    // Vendor-supplier linking should be done separately, not during invoice creation
                }
                await updateSuppliersHelper(updateData)
                await loadSuppliers()
            } catch (error) {
                // Don't fail the whole operation if supplier update fails
            }
        }
        return selectedSupplier
    }

    // Create new products that are marked as __isNew__ in the products table
    const createNewProducts = async (supplierId) => {
        const updatedProducts = [...productsInTable]
        
        for (let i = 0; i < updatedProducts.length; i++) {
            const product = updatedProducts[i]
            
            if (product.__isNew__ && !product.id) {
                try {
                    const finalDose = product.dose || product.strength
                    // Note: Do NOT include 'id' field for new products - backend will create one
                    const productDetailsForBackend = {
                        name: product.productName,
                        generic_name: product.genericName,
                        strength: product.strength || null,
                        dose: finalDose,
                        category: product.category,
                        barcode: product.barcode || null,
                        type: product.productType || 'Pharmaceutical',
                        origin: product.origin || 'Local',
                        is_prescription_required: product.prescriptionRequired === 'Yes',
                        prescription_note: product.prescriptionDetails || null,
                        cost_per_unit: parseFloat(product.costPerUnit),
                        vat: parseFloat(product.vatPercentage || 0),
                        new_cost_per_unit: parseFloat(product.costPerUnit),
                        rate_per_unit: parseFloat(product.ratePerUnit),
                        medicine_per_strips: product.medPerStrip || 1
                    }
                    
                    const requestData = {
                        supplier_id: parseInt(supplierId),
                        product_details: productDetailsForBackend
                    }
                    
                    const formData = new FormData()
                    formData.append('data', JSON.stringify(requestData))
                    
                    const createdProduct = await updateProductDataHelper(formData)
                    
                    // Update product in the array with created ID
                    updatedProducts[i] = {
                        ...product,
                        id: String(createdProduct.id),
                        __isNew__: false
                    }
                } catch (error) {
                    throw new Error(`Failed to create product "${product.productName}": ${error.message || error}`)
                }
            }
        }
        
        // Update the products table state with created product IDs
        setProductsInTable(updatedProducts)
        return updatedProducts
    }

    const handleConfirmHold = async () => {
        setShowHoldConfirmation(false)
        if (!validateForm()) return

        try {
            setLoading(true)
            
            // 1. Create or update vendor first
            const updatedVendor = await updateVendorDetails()
            const vendorId = updatedVendor?.value || selectedVendor?.value
            
            // 2. Create or update supplier (pass vendor ID)
            const updatedSupplier = await updateSupplierDetails(vendorId)
            const supplierId = updatedSupplier?.value || selectedSupplier?.value
            
            // 3. Create new products (pass supplier ID) - returns updated products array
            let updatedProducts = productsInTable
            if (supplierId) {
                updatedProducts = await createNewProducts(supplierId)
            }
            
            // Pass updated products and supplier ID to avoid React state timing issues
            const data = prepareSubmitData('HOLD', updatedProducts, supplierId)
            await addProductReceivingHelper(data)

            // If no error thrown, it was successful
            clearDraft(DRAFT_KEY)
            dispatch(addProductReceivingAlertAction(true))
            setTimeout(() => {
                dispatch(removeProductReceivingAlertAction())
            }, 5000)
            navigate('/home/details')
        } catch (error) {            alert(error.message || error || 'Failed to save product receiving')
        } finally {
            setLoading(false)
        }
    }

    const handleCancelHold = () => {
        setShowHoldConfirmation(false)
    }

    // Handle confirm button click (for new invoices)
    const handleConfirm = async () => {
        setShowConfirmConfirmation(true)
    }

    const handleConfirmConfirmSubmit = async () => {
        setShowConfirmConfirmation(false)
        if (!validateForm()) return

        setLoading(true)
        
        // 1. Create or update vendor first
        const updatedVendor = await updateVendorDetails()
        const vendorId = updatedVendor?.value || selectedVendor?.value
        
        // 2. Create or update supplier (pass vendor ID)
        const updatedSupplier = await updateSupplierDetails(vendorId)
        const supplierId = updatedSupplier?.value || selectedSupplier?.value
        
        // 3. Create new products (pass supplier ID) - returns updated products array
        let updatedProducts = productsInTable
        if (supplierId) {
            updatedProducts = await createNewProducts(supplierId)
        }
        
        // Submit with RECEIVED status directly
        const data = prepareSubmitData('RECEIVED', updatedProducts, supplierId)
        await addProductReceivingHelper(data)

        // If no error thrown, it was successful
        clearDraft(DRAFT_KEY)
        dispatch(addProductReceivingAlertAction(true))
        setTimeout(() => {
            dispatch(removeProductReceivingAlertAction())
        }, 5000)
        navigate('/home/details')
        setLoading(false)
    }

    const handleCancelConfirm = () => {
        setShowConfirmConfirmation(false)
    }

    const handleSave = async () => {
        if (!validateForm()) return
        if (!isEditMode) {
            alert('Save is only available in edit mode')
            return
        }

        try {
            setLoading(true)
            
            // 1. Create or update vendor first
            const updatedVendor = await updateVendorDetails()
            const vendorId = updatedVendor?.value || selectedVendor?.value
            
            // 2. Create or update supplier (pass vendor ID)
            const updatedSupplier = await updateSupplierDetails(vendorId)
            const supplierId = updatedSupplier?.value || selectedSupplier?.value
            
            // 3. Create new products (pass supplier ID) - returns updated products array
            let updatedProducts = productsInTable
            if (supplierId) {
                updatedProducts = await createNewProducts(supplierId)
            }
            
            // Preserve the current invoice status (don't change HOLD to RECEIVED)
            // Pass updated products and supplier ID to avoid React state timing issues
            const data = prepareSubmitData(currentInvoiceStatus, updatedProducts, supplierId)
            // Use update helper for edit mode
            await updateProductReceivingHelper(data)

            // If no error thrown, it was successful
            clearDraft(DRAFT_KEY)
            dispatch(addProductReceivingAlertAction(true))
            setTimeout(() => {
                dispatch(removeProductReceivingAlertAction())
            }, 5000)
            navigate('/home/details')
        } catch (error) {            alert(error.message || error || 'Failed to update product receiving')
        } finally {
            setLoading(false)
        }
    }

    // Handle confirm directly from edit mode (for HOLD invoices)
    const handleConfirmFromEdit = async () => {
        if (!validateForm()) return

        if (!isEditMode || !productReceivingId) {
            alert('Confirm is only available for existing invoices')
            return
        }

        if (currentInvoiceStatus !== 'HOLD') {
            alert('Only HOLD invoices can be confirmed')
            return
        }

        try {
            setLoading(true)
            
            // 1. Create or update vendor first
            const updatedVendor = await updateVendorDetails()
            const vendorId = updatedVendor?.value || selectedVendor?.value
            
            // 2. Create or update supplier (pass vendor ID)
            const updatedSupplier = await updateSupplierDetails(vendorId)
            const supplierId = updatedSupplier?.value || selectedSupplier?.value
            
            // 3. Create new products (pass supplier ID) - returns updated products array
            let updatedProducts = productsInTable
            if (supplierId) {
                updatedProducts = await createNewProducts(supplierId)
            }
            
            // Change status to RECEIVED when confirming
            const data = prepareSubmitData('RECEIVED', updatedProducts, supplierId)
            // Use update helper for edit mode
            await updateProductReceivingHelper(data)

            // If no error thrown, it was successful
            clearDraft(DRAFT_KEY)
            dispatch(addProductReceivingAlertAction(true))
            setTimeout(() => {
                dispatch(removeProductReceivingAlertAction())
            }, 5000)
            navigate('/home/details')
        } catch (error) {
            alert(error.message || error || 'Failed to confirm product receiving')
        } finally {
            setLoading(false)
        }
    }

    const handleDeleteInvoice = async () => {
        if (!isEditMode || !productReceivingId) {
            alert('Delete is only available for existing invoices')
            return
        }

        const confirmed = window.confirm('Are you sure you want to delete this invoice? This action cannot be undone.')
        if (!confirmed) return

        try {
            setLoading(true)
            await deleteProductReceivingHelper({
                product_receiving_id: productReceivingId
            })

            alert('Invoice deleted successfully')
            navigate('/home/details')
        } catch (error) {            alert(error.message || error || 'Failed to delete invoice')
        } finally {
            setLoading(false)
        }
    }

    const handleClose = () => {
        setShowCloseConfirmation(true)
    }

    const handleConfirmClose = () => {
        setShowCloseConfirmation(false)
        clearDraft(DRAFT_KEY)
        navigate('/home/details')
    }

    const handleCancelClose = () => {
        setShowCloseConfirmation(false)
    }

    if (loadingInvoice) {
        return (
            <div className="containerHome">
                {!isMobile && <Header />}
                <SideMenu activeItem={SIDEBAR_CONST.HOME} component="HOME_DETAILS" />
                <div className="pageBody dFlex">
                    {!isMobile && (
                        <div className="pageBodyLeftPanel sticky">
                            <div className="chainNavigationWrapper">
                                <ul className="chainNavigation">
                                    {HOME_NAVIGATION_ITEMS.map(item => {
                                        if (permissions?.features?.[item.feature] !== PERMISSION_TYPE.RESTRICTED) {
                                            return (
                                                <li
                                                    key={item.name}
                                                    className={`chainItem ${item.name === HOME_CONST.PRODUCT_RECEIVING ? 'active' : ''}`}
                                                    onClick={() => {
                                                        dispatch(activeHomeDetailNavigation(item.name))
                                                        navigate('/home/details')
                                                    }}
                                                >
                                                    {item.homeDetailIcon}
                                                    <span>{item.name}</span>
                                                </li>
                                            )
                                        }
                                        return null
                                    })}
                                </ul>
                            </div>
                        </div>
                    )}
                    <div className="pageBodyRightPanel">
                        <div className="loading-message">Loading invoice data...</div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="containerHome">
            {!isMobile && <Header />}
            <SideMenu activeItem={SIDEBAR_CONST.HOME} component="HOME_DETAILS" />
            <div className="pageBody dFlex">
                {!isMobile && (
                    <div className="pageBodyLeftPanel sticky">
                        <div className="chainNavigationWrapper">
                            <ul className="chainNavigation">
                                {HOME_NAVIGATION_ITEMS.map(item => {
                                    if (permissions?.features?.[item.feature] !== PERMISSION_TYPE.RESTRICTED) {
                                        return (
                                            <li
                                                key={item.name}
                                                className={`chainItem ${item.name === HOME_CONST.PRODUCT_RECEIVING ? 'active' : ''}`}
                                                onClick={() => {
                                                    dispatch(activeHomeDetailNavigation(item.name))
                                                    navigate('/home/details')
                                                }}
                                            >
                                                {item.homeDetailIcon}
                                                <span>{item.name}</span>
                                            </li>
                                        )
                                    }
                                    return null
                                })}
                            </ul>
                        </div>
                    </div>
                )}
                <div className="pageBodyRightPanel">
                    <div className="product-receiving-form-container">
                        <div className="form-header">
                            <h2>{isEditMode ? 'Edit Product Receiving' : 'New Product Receiving'}</h2>
                        </div>

                        {/* Section 1: Invoice Information */}
                        <div className="form-section">
                            <h3>Invoice Information</h3>
                            <div className="form-grid">
                                <div className="form-field">
                                    <label>Invoice Number <span style={{ color: 'red' }}>*</span></label>
                                    <input
                                        type="text"
                                        ref={invoiceNumberRef}
                                        value={invoiceNumber}
                                        onChange={(e) => setInvoiceNumber(e.target.value)}
                                        onKeyDown={(e) => handleFieldKeyDown(e, vendorRef)}
                                        placeholder="Enter invoice number"
                                    />
                                </div>
                                <div className="form-field">
                                    <label>Vendor ID</label>
                                    <input
                                        type="text"
                                        value={selectedVendor?.vendor_id || ''}
                                        placeholder="Auto-generated"
                                        disabled
                                    />
                                </div>
                                <div className="form-field">
                                    <label>Vendor</label>
                                    <CreatableSelect
                                        ref={vendorRef}
                                        isClearable
                                        options={vendorOptions}
                                        value={selectedVendor}
                                        onChange={handleVendorChange}
                                        onMenuOpen={() => setVendorMenuOpen(true)}
                                        onMenuClose={() => setVendorMenuOpen(false)}
                                        onKeyDown={(e) => handleSelectKeyDown(e, vendorCountryCodeRef, vendorMenuOpen)}
                                        placeholder="Select or Create Vendor"
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                            placeholder: (base) => ({ ...base, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
                                            singleValue: (base) => ({ ...base, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
                                            control: (base) => ({ ...base, minWidth: '180px' })
                                        }}
                                    />
                                </div>
                                <div className="form-field">
                                    <label>Vendor Contact</label>
                                    <div className="contact-input">
                                        <input
                                            type="text"
                                            ref={vendorCountryCodeRef}
                                            value={vendorCountryCode}
                                            onChange={(e) => setVendorCountryCode(e.target.value)}
                                            onKeyDown={(e) => handleFieldKeyDown(e, vendorContactRef)}
                                            placeholder="+880"
                                        />
                                        <input
                                            type="text"
                                            ref={vendorContactRef}
                                            value={vendorContact}
                                            onChange={(e) => setVendorContact(e.target.value)}
                                            onKeyDown={(e) => handleFieldKeyDown(e, vendorAddressRef)}
                                            placeholder="Contact number"
                                        />
                                    </div>
                                </div>
                                <div className="form-field">
                                    <label>Vendor Address</label>
                                    <input
                                        type="text"
                                        ref={vendorAddressRef}
                                        value={vendorAddress}
                                        onChange={(e) => setVendorAddress(e.target.value)}
                                        onKeyDown={(e) => handleFieldKeyDown(e, supplierRef)}
                                        placeholder="Enter vendor address"
                                    />
                                </div>
                                <div className="form-field">
                                    <label>Supplier ID</label>
                                    <input
                                        type="text"
                                        value={selectedSupplier?.supplier_id || selectedSupplier?.value || ''}
                                        placeholder="Auto-generated"
                                        disabled
                                    />
                                </div>
                                <div className="form-field">
                                    <label>Supplier <span style={{ color: 'red' }}>*</span></label>
                                    <CreatableSelect
                                        ref={supplierRef}
                                        isClearable
                                        options={supplierOptions}
                                        value={selectedSupplier}
                                        onChange={handleSupplierChange}
                                        onMenuOpen={() => setSupplierMenuOpen(true)}
                                        onMenuClose={() => setSupplierMenuOpen(false)}
                                        onKeyDown={(e) => handleSelectKeyDown(e, supplierCountryCodeRef, supplierMenuOpen)}
                                        placeholder="Select or Create Supplier"
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                            placeholder: (base) => ({ ...base, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
                                            singleValue: (base) => ({ ...base, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
                                            control: (base) => ({ ...base, minWidth: '180px' })
                                        }}
                                    />
                                </div>
                                <div className="form-field">
                                    <label>Supplier Contact <span style={{ color: 'red' }}>*</span></label>
                                    <div className="contact-input">
                                        <input
                                            type="text"
                                            ref={supplierCountryCodeRef}
                                            value={supplierCountryCode}
                                            onChange={(e) => setSupplierCountryCode(e.target.value)}
                                            onKeyDown={(e) => handleFieldKeyDown(e, supplierContactRef)}
                                            placeholder="+880"
                                        />
                                        <input
                                            type="text"
                                            ref={supplierContactRef}
                                            value={supplierContact}
                                            onChange={(e) => setSupplierContact(e.target.value)}
                                            onKeyDown={(e) => handleFieldKeyDown(e, supplierAddressRef)}
                                            placeholder="Contact number"
                                        />
                                    </div>
                                </div>
                                <div className="form-field full-width">
                                    <label>Supplier Address <span style={{ color: 'red' }}>*</span></label>
                                    <input
                                        type="text"
                                        ref={supplierAddressRef}
                                        value={supplierAddress}
                                        onChange={(e) => setSupplierAddress(e.target.value)}
                                        onKeyDown={(e) => handleFieldKeyDown(e, productNameRef)}
                                        placeholder="Enter supplier address"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Section 2: Product Selection */}
                        <div className="form-section">
                            <h3>Product Details</h3>

                            <div className="form-grid">
                                {/* Row 1: Product ID, Product Name, Generic Name */}
                                <div className="form-field">
                                    <label>Product ID</label>
                                    <input
                                        type="text"
                                        value={productId}
                                        onChange={(e) => setProductId(e.target.value)}
                                        placeholder="Enter product ID"
                                        disabled
                                    />
                                </div>
                                <div className="form-field">
                                    <label>Product Name <span style={{ color: 'red' }}>*</span></label>
                                    <AsyncCreatableSelect
                                        ref={productNameRef}
                                        isClearable
                                        loadOptions={loadProductOptions}
                                        defaultOptions={productOptions}
                                        value={selectedProduct ? {
                                            ...selectedProduct,
                                            dose: dose || selectedProduct.dose,
                                            strength: strength || selectedProduct.strength,
                                            category: category || selectedProduct.category
                                        } : null}
                                        onChange={handleProductSelect}
                                        onMenuOpen={() => {
                                            setProductMenuOpen(true)
                                            if (productOptions.length === 0) loadInitialProducts()
                                        }}
                                        onMenuClose={() => setProductMenuOpen(false)}
                                        onMenuScrollToBottom={() => loadMoreProducts()}
                                        onKeyDown={(e) => handleSelectKeyDown(e, genericNameRef, productMenuOpen)}
                                        filterOption={(option, inputValue) => {
                                            if (!inputValue) return true
                                            const label = option.label?.toLowerCase() || ''
                                            return label.includes(inputValue.toLowerCase())
                                        }}
                                        getOptionLabel={(option) => {
                                            if (option.__isNew__) return option.label
                                            const name = option.label || option.name || ''
                                            const optDose = option.dose || ''
                                            const optStrength = option.strength || ''
                                            const optCategory = option.category || ''
                                            return `${name}${optDose ? ` (${optDose})` : ''}${optStrength ? `-${optStrength}` : ''}${optCategory ? `-${optCategory}` : ''}`
                                        }}
                                        placeholder="Search or create product"
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                            placeholder: (base) => ({ ...base, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '13px' }),
                                            singleValue: (base) => ({ ...base, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '13px' }),
                                            control: (base) => ({ ...base, minWidth: '180px' }),
                                            option: (base) => ({ ...base, fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
                                            menu: (base) => ({ ...base, width: 'auto', minWidth: '100%' })
                                        }}
                                    />
                                </div>
                                <div className="form-field">
                                    <label>Generic Name <span style={{ color: 'red' }}>*</span></label>
                                    <input
                                        type="text"
                                        ref={genericNameRef}
                                        value={genericName}
                                        onChange={(e) => setGenericName(e.target.value)}
                                        onKeyDown={(e) => handleFieldKeyDown(e, sizeOrMedType === 'Size' ? sizeOrMedValueRef : strengthRef)}
                                        placeholder="Enter generic name"
                                    />
                                </div>

                                {/* Row 2: Size/Med Per Strip (with radio), Strength, Category */}
                                <div className="form-field">
                                    <label>Type <span style={{ color: 'red' }}>*</span></label>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <div className="radio-group">
                                            <label>
                                                <input
                                                    type="radio"
                                                    value="Size"
                                                    checked={sizeOrMedType === 'Size'}
                                                    onChange={(e) => setSizeOrMedType(e.target.value)}
                                                />
                                                Size
                                            </label>
                                            <label>
                                                <input
                                                    type="radio"
                                                    value="Med/Strip"
                                                    checked={sizeOrMedType === 'Med/Strip'}
                                                    onChange={(e) => {
                                                        setSizeOrMedType(e.target.value)
                                                        setSizeOrMedValue('') // Clear size value when switching to Med/Strip
                                                    }}
                                                />
                                                Med/Strip
                                            </label>
                                        </div>
                                        {/* Only show size input when Size is selected */}
                                        {sizeOrMedType === 'Size' && (
                                            <input
                                                type="text"
                                                ref={sizeOrMedValueRef}
                                                value={sizeOrMedValue}
                                                onChange={(e) => setSizeOrMedValue(e.target.value)}
                                                onKeyDown={(e) => handleFieldKeyDown(e, strengthRef)}
                                                placeholder="Enter size (e.g., 100ml, 12)"
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className="form-field">
                                    <label>Strength</label>
                                    <input
                                        type="text"
                                        ref={strengthRef}
                                        value={strength}
                                        onChange={(e) => setStrength(e.target.value)}
                                        onKeyDown={(e) => handleFieldKeyDown(e, categoryRef)}
                                        placeholder="e.g., 120mg/5ml"
                                    />
                                </div>
                                <div className="form-field">
                                    <label>Category <span style={{ color: 'red' }}>*</span></label>
                                    <Select
                                        ref={categoryRef}
                                        options={categoryOptions}
                                        value={category ? { value: category, label: category } : null}
                                        onChange={(option) => setCategory(option?.value || '')}
                                        onMenuOpen={() => setCategoryMenuOpen(true)}
                                        onMenuClose={() => setCategoryMenuOpen(false)}
                                        onKeyDown={(e) => handleSelectKeyDown(e, barcodeInputRef, categoryMenuOpen)}
                                        placeholder="Select category"
                                        isClearable
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                            placeholder: (base) => ({ ...base, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
                                            singleValue: (base) => ({ ...base, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
                                            control: (base) => ({ ...base, minWidth: '140px' })
                                        }}
                                    />
                                </div>

                                {/* Row 3: Barcode, Batch Number, Expiry Date */}
                                <div className="form-field">
                                    <label>Barcode <span style={{ color: 'red' }}>*</span></label>
                                    <div style={{ display: 'flex', gap: '5px', flexDirection: 'column' }}>
                                        <div style={{ display: 'flex', gap: '5px' }}>
                                            <input
                                                type="text"
                                                ref={barcodeInputRef}
                                                value={barcode}
                                                onChange={(e) => setBarcode(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault()
                                                        // First do barcode lookup, then move to next field
                                                        handleBarcodeLookup()
                                                        batchNumberRef.current?.focus()
                                                    }
                                                }}
                                                placeholder="Enter barcode and press Enter"
                                                style={{ flex: 1 }}
                                            />
                                        </div>
                                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                            {/* For new products (no existing product selected) */}
                                            {!selectedProduct?.value && (
                                                <button
                                                    type="button"
                                                    onClick={handleGenerateBarcode}
                                                    className="btn btn-secondary"
                                                    style={{ padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '40px' }}
                                                    title="Generate Barcode"
                                                >
                                                    <div style={{ color: 'inherit' }}>
                                                        <BarcodeIcon />
                                                    </div>
                                                </button>
                                            )}
                                            {/* For existing products (product selected from dropdown) */}
                                            {selectedProduct?.value && (
                                                <>
                                                    {barcode ? (
                                                        <>
                                                            <button
                                                                type="button"
                                                                onClick={handleViewBarcode}
                                                                className="btn btn-secondary"
                                                                style={{ padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '40px' }}
                                                                title="View Barcode"
                                                            >
                                                                <div style={{ color: 'inherit' }}>
                                                                    <EyeOpen />
                                                                </div>
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={handleRemoveBarcode}
                                                                className="btn btn-danger"
                                                                style={{ padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '40px' }}
                                                                title="Remove Barcode"
                                                            >
                                                                <div style={{ color: 'inherit' }}>
                                                                    <TrashIcon />
                                                                </div>
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={handleGenerateNewBarcode}
                                                                className="btn btn-secondary"
                                                                style={{ padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '40px' }}
                                                                title="Generate New Barcode"
                                                            >
                                                                <div style={{ color: 'inherit' }}>
                                                                    <AddIcon />
                                                                </div>
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={handleGenerateBarcode}
                                                            className="btn btn-secondary"
                                                            style={{ padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '40px' }}
                                                            title="Generate Barcode"
                                                        >
                                                            <div style={{ color: 'inherit' }}>
                                                                <BarcodeIcon />
                                                            </div>
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="form-field">
                                    <label>Batch Number <span style={{ color: 'red' }}>*</span></label>
                                    <input
                                        type="text"
                                        ref={batchNumberRef}
                                        value={batchNumber}
                                        onChange={(e) => setBatchNumber(e.target.value)}
                                        onKeyDown={(e) => handleFieldKeyDown(e, expiryDateRef)}
                                        placeholder="Enter batch number"
                                    />
                                </div>
                                <div className="form-field">
                                    <label>Expiry Date <span style={{ color: 'red' }}>*</span></label>
                                    <input
                                        type="date"
                                        ref={expiryDateRef}
                                        value={expiryDate}
                                        onChange={(e) => setExpiryDate(e.target.value)}
                                        onKeyDown={(e) => handleFieldKeyDown(e, quantityRef)}
                                        placeholder="dd/mm/yyyy"
                                    />
                                </div>

                                {/* Row 4: Quantity, Bonus Quantity, Total TP */}
                                <div className="form-field">
                                    <label>Quantity <span style={{ color: 'red' }}>*</span></label>
                                    <input
                                        type="number"
                                        ref={quantityRef}
                                        value={quantity}
                                        onChange={(e) => setQuantity(e.target.value)}
                                        onKeyDown={(e) => handleFieldKeyDown(e, bonusQuantityRef)}
                                        placeholder="Enter quantity"
                                        min="1"
                                    />
                                </div>
                                <div className="form-field">
                                    <label>Bonus Quantity</label>
                                    <input
                                        type="number"
                                        ref={bonusQuantityRef}
                                        value={bonusQuantity}
                                        onChange={(e) => setBonusQuantity(e.target.value)}
                                        onKeyDown={(e) => handleFieldKeyDown(e, totalTPRef)}
                                        placeholder="Enter bonus quantity"
                                        min="0"
                                    />
                                </div>
                                <div className="form-field">
                                    <label>Total TP <span style={{ color: 'red' }}>*</span></label>
                                    <input
                                        type="number"
                                        ref={totalTPRef}
                                        value={totalTP}
                                        onChange={(e) => setTotalTP(e.target.value)}
                                        onKeyDown={(e) => handleFieldKeyDown(e, totalVATRef)}
                                        placeholder="Enter total TP"
                                        step="0.01"
                                        min="0"
                                    />
                                </div>

                                {/* Row 5: Total VAT, CPU (read-only), RPU */}
                                <div className="form-field">
                                    <label>Total VAT <span style={{ color: 'red' }}>*</span></label>
                                    <input
                                        type="number"
                                        ref={totalVATRef}
                                        value={totalVAT}
                                        onChange={(e) => setTotalVAT(e.target.value)}
                                        onKeyDown={(e) => handleFieldKeyDown(e, rpuRef)}
                                        placeholder="Enter total VAT"
                                        step="0.01"
                                        min="0"
                                    />
                                </div>
                                <div className="form-field">
                                    <label>CPU <span style={{ color: 'red' }}>*</span></label>
                                    <input
                                        type="number"
                                        value={costPerUnit}
                                        placeholder="Auto-calculated"
                                        step="0.01"
                                        min="0"
                                        readOnly
                                        style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
                                    />
                                </div>
                                <div className="form-field">
                                    <label>RPU <span style={{ color: 'red' }}>*</span></label>
                                    <input
                                        type="number"
                                        ref={rpuRef}
                                        value={ratePerUnit}
                                        onChange={(e) => setRatePerUnit(e.target.value)}
                                        onKeyDown={handleLastFieldKeyDown}
                                        placeholder="Enter RPU"
                                        step="0.01"
                                        min="0"
                                    />
                                </div>

                                {/* Row 6: Product Type, Prescription (only for Pharmaceutical), Origin */}
                                <div className="form-field">
                                    <label>Product Type</label>
                                    <div className="radio-group">
                                        <label>
                                            <input
                                                type="radio"
                                                value="Pharmaceutical"
                                                checked={productType === 'Pharmaceutical'}
                                                onChange={(e) => setProductType(e.target.value)}
                                            />
                                            Pharmaceutical
                                        </label>
                                        <label>
                                            <input
                                                type="radio"
                                                value="Non Pharmaceutical"
                                                checked={productType === 'Non Pharmaceutical'}
                                                onChange={(e) => {
                                                    setProductType(e.target.value)
                                                    // Clear prescription fields when switching to Non-Pharmaceutical
                                                    setPrescriptionRequired('No')
                                                    setPrescriptionDetails('')
                                                }}
                                            />
                                            Non Pharmaceutical
                                        </label>
                                    </div>
                                </div>
                                {/* Only show Prescription field for Pharmaceutical products */}
                                {productType === 'Pharmaceutical' && (
                                    <div className="form-field">
                                        <label>Prescription</label>
                                        <div className="radio-group">
                                            <label>
                                                <input
                                                    type="radio"
                                                    value="Yes"
                                                    checked={prescriptionRequired === 'Yes'}
                                                    onChange={(e) => setPrescriptionRequired(e.target.value)}
                                                />
                                                Yes
                                            </label>
                                            <label>
                                                <input
                                                    type="radio"
                                                    value="No"
                                                    checked={prescriptionRequired === 'No'}
                                                    onChange={(e) => {
                                                        setPrescriptionRequired(e.target.value)
                                                        setPrescriptionDetails('') // Clear details when No
                                                    }}
                                                />
                                                No
                                            </label>
                                        </div>
                                    </div>
                                )}
                                <div className="form-field">
                                    <label>Origin</label>
                                    <div className="radio-group">
                                        <label>
                                            <input
                                                type="radio"
                                                value="Local"
                                                checked={origin === 'Local'}
                                                onChange={(e) => setOrigin(e.target.value)}
                                            />
                                            Local
                                        </label>
                                        <label>
                                            <input
                                                type="radio"
                                                value="Foreign"
                                                checked={origin === 'Foreign'}
                                                onChange={(e) => setOrigin(e.target.value)}
                                            />
                                            Foreign
                                        </label>
                                    </div>
                                </div>

                                {/* Conditional: Prescription Details (only show when Product Type = Pharmaceutical and Prescription = Yes) */}
                                {productType === 'Pharmaceutical' && prescriptionRequired === 'Yes' && (
                                    <div className="form-field full-width">
                                        <label>Prescription Details</label>
                                        <input
                                            type="text"
                                            value={prescriptionDetails}
                                            onChange={(e) => setPrescriptionDetails(e.target.value)}
                                            placeholder="Enter prescription details"
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="form-actions">
                                <button
                                    type="button"
                                    onClick={handleAddProduct}
                                    className="btn-primary"
                                >
                                    {selectedTableRowIndex !== null ? 'Update Product' : 'Add'}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSaveProduct}
                                    className="btn-primary"
                                >
                                    Save
                                </button>
                                <button
                                    type="button"
                                    onClick={handleDeleteProduct}
                                    className="btn-danger"
                                    disabled={selectedTableRowIndex === null}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>

                        {/* Section 3: Products Table */}
                        <div className="form-section">
                            <h3>Product Information</h3>
                            {productsInTable.length === 0 ? (
                                <p className="empty-message">No products added yet. Add products using the form above.</p>
                            ) : (
                                <div className="products-table-wrapper">
                                    <table className="products-table">
                                        <thead>
                                            <tr>
                                                <th>SL</th>
                                                <th>Product Name (size)-Strength</th>
                                                <th>Category</th>
                                                <th>Batch No</th>
                                                <th>Expiry Date</th>
                                                <th>Qty</th>
                                                <th>Bonus Qty</th>
                                                <th>CPU</th>
                                                <th>RPU</th>
                                                <th>Sub Total</th>
                                                <th>Supplier/Manufacturer</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {productsInTable.map((product, index) => {
                                                // Format: 
                                                // Size type: Name (size)-Strength
                                                // Med/Strip type: Name-Strength (no brackets)
                                                let formattedProductName = product.productName
                                                if (product.sizeOrMedType === 'Size' && product.dose) {
                                                    formattedProductName += ` (${product.dose})`
                                                }
                                                if (product.strength) {
                                                    formattedProductName += `-${product.strength}`
                                                }
                                                
                                                return (
                                                    <tr
                                                        key={index}
                                                        onClick={() => handleTableRowClick(index)}
                                                        onDoubleClick={() => handleTableRowDoubleClick(index)}
                                                        className={selectedTableRowIndex === index ? 'selected' : ''}
                                                        style={{ cursor: 'pointer' }}
                                                        title="Double-click to edit"
                                                    >
                                                        <td>{index + 1}</td>
                                                        <td>{formattedProductName}</td>
                                                        <td>{product.category || ''}</td>
                                                        <td>{product.batchNumber}</td>
                                                        <td>{formatDateTime(product.expiryDate, 'date')}</td>
                                                        <td>{product.quantity}</td>
                                                        <td>{product.bonusQuantity}</td>
                                                        <td>{product.costPerUnit.toFixed(2)}</td>
                                                        <td>{product.ratePerUnit.toFixed(2)}</td>
                                                        <td>{product.subtotal.toFixed(2)}</td>
                                                        <td>{product.supplierName || ''}</td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Section 4: Invoice Summary */}
                        <div className="form-section">
                            <h3>Invoice Summary</h3>
                            <div className="invoice-summary">
                                <div className="summary-row">
                                    <label>Invoice Value:</label>
                                    <span className="summary-value">{invoiceValue.toFixed(2)}</span>
                                </div>
                                <div className="summary-row">
                                    <label>Total TP:</label>
                                    <span className="summary-value">{totalTPValue.toFixed(2)}</span>
                                </div>
                                <div className="summary-row">
                                    <label>Total VAT:</label>
                                    <span className="summary-value">{totalVATValue.toFixed(2)}</span>
                                </div>
                                <div className="summary-row">
                                    <label>Trade Discount %:</label>
                                    <input
                                        type="number"
                                        value={tradeDiscountPercentage}
                                        onChange={(e) => handleTradeDiscountPercentageChange(e.target.value)}
                                        step="0.01"
                                        min="0"
                                        max="100"
                                        style={{ width: '150px' }}
                                    />
                                </div>
                                <div className="summary-row">
                                    <label>Trade Discount Amount:</label>
                                    <input
                                        type="number"
                                        value={tradeDiscountAmount}
                                        onChange={(e) => handleTradeDiscountAmountChange(e.target.value)}
                                        step="0.01"
                                        min="0"
                                        style={{ width: '150px' }}
                                    />
                                </div>
                                <div className="summary-row total-row">
                                    <label>Net Value:</label>
                                    <span className="summary-value">{netValue.toFixed(2)}</span>
                                </div>
                                <div className="summary-row">
                                    <label>Pay Mode:</label>
                                    <Select
                                        options={PAYMENT_MODE_OPTIONS_PRODUCT_RECEIVING}
                                        value={PAYMENT_MODE_OPTIONS_PRODUCT_RECEIVING.find(option => option.name === paymentMode) || null}
                                        onChange={(option) => {
                                            setPaymentMode(option.name)
                                            // Clear selected bank if payment mode is not Cash in account
                                            if (option.name !== 'CASH_IN_ACCOUNT') {
                                                setSelectedBank(null)
                                            }
                                        }}
                                        getOptionLabel={(option) => option.label}
                                        getOptionValue={(option) => option.name}
                                        menuPortalTarget={document.body}
                                        styles={{ 
                                            container: (base) => ({ ...base, width: '200px' }),
                                            menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                        }}
                                    />
                                </div>
                                {paymentMode === 'CASH_IN_ACCOUNT' && (
                                    <div className="summary-row">
                                        <label>Bank:</label>
                                        {banksList.length > 0 ? (
                                            <Select
                                                options={banksList}
                                                value={selectedBank}
                                                onChange={(option) => setSelectedBank(option ? {
                                                    value: option.id,
                                                    label: option.bank_name,
                                                    id: option.id,
                                                    bank_name: option.bank_name
                                                } : null)}
                                                getOptionLabel={(option) => option.bank_name}
                                                getOptionValue={(option) => option.id}
                                                placeholder="Select Bank"
                                                isClearable
                                                menuPortalTarget={document.body}
                                                styles={{ 
                                                    container: (base) => ({ ...base, width: '200px' }),
                                                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                                }}
                                            />
                                        ) : (
                                            <span style={{ color: '#d9534f', fontSize: '13px' }}>
                                                No Bank account is added. Please add Bank details first in Settings.
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Section 5: Action Buttons */}
                        <div className="form-section">
                            <div className="form-actions-bottom">
                                {!isEditMode && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={handleHold}
                                            className="btn-secondary"
                                            disabled={loading}
                                        >
                                            {loading ? 'Saving...' : 'Hold'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleConfirm}
                                            className="btn-success"
                                            disabled={loading}
                                        >
                                            {loading ? 'Confirming...' : 'Confirm'}
                                        </button>
                                    </>
                                )}
                                {isEditMode && (
                                    <button
                                        type="button"
                                        onClick={handleSave}
                                        className="btn-primary"
                                        disabled={loading}
                                    >
                                        {loading ? 'Updating...' : 'Save'}
                                    </button>
                                )}
                                {isEditMode && currentInvoiceStatus === 'HOLD' && (
                                    <button
                                        type="button"
                                        onClick={handleConfirmFromEdit}
                                        className="btn-success"
                                        disabled={loading}
                                    >
                                        {loading ? 'Confirming...' : 'Confirm'}
                                    </button>
                                )}
                                {isEditMode && (
                                    <button
                                        type="button"
                                        onClick={handleDeleteInvoice}
                                        className="btn-danger"
                                        disabled={loading}
                                    >
                                        {loading ? 'Deleting...' : 'Delete'}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="btn-secondary"
                                    disabled={loading}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Barcode SideDrawer */}
            <SideDrawer
                handleDrawerToggle={handleBarcodeDrawerToggle}
                open={barcodeDrawerOpen}
                downloadButton={true}
                downloadText="Save & Print"
                drawerHeading="Product Barcode"
                handleClick={handleDownloadBarcode}
            >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px', minHeight: '300px' }}>
                    <div ref={svgRef} style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        {barcode && (
                            <Barcode
                                value={barcode}
                                options={{
                                    format: 'auto',
                                    displayValue: true,
                                    width: 2,
                                    height: 100
                                }}
                            />
                        )}
                    </div>
                </div>
            </SideDrawer>

            {/* Hold Confirmation Dialog */}
            {showHoldConfirmation && (
                <PopupCard
                    header="Hold Invoice"
                    body="Are you sure you want to hold this invoice?">
                    <button
                        className="btn btnPrimaryOutlined dFlex"
                        onClick={handleCancelHold}
                        disabled={loading}>
                        Cancel
                    </button>
                    <button
                        className="btn btnPrimary dFlex"
                        onClick={handleConfirmHold}
                        disabled={loading}>
                        {loading ? 'Saving...' : 'Hold'}
                    </button>
                </PopupCard>
            )}

            {/* Confirm Confirmation Dialog */}
            {showConfirmConfirmation && (
                <PopupCard
                    header="Confirm Invoice"
                    body="Are you sure you want to confirm this invoice? This will save the invoice as Received.">
                    <button
                        className="btn btnPrimaryOutlined dFlex"
                        onClick={handleCancelConfirm}
                        disabled={loading}>
                        Cancel
                    </button>
                    <button
                        className="btn btnPrimary dFlex"
                        onClick={handleConfirmConfirmSubmit}
                        disabled={loading}>
                        {loading ? 'Confirming...' : 'Confirm'}
                    </button>
                </PopupCard>
            )}

            {/* Close Confirmation Dialog */}
            {showCloseConfirmation && (
                <PopupCard
                    header="End Receiving Process"
                    body="Are you sure you want to end the receiving process? Any unsubmitted data will be lost.">
                    <button
                        className="btn btnPrimaryOutlined dFlex"
                        onClick={handleCancelClose}>
                        Cancel
                    </button>
                    <button
                        className="btn btnDanger dFlex"
                        onClick={handleConfirmClose}>
                        End Process
                    </button>
                </PopupCard>
            )}
        </div>
    )
}

export default ProductReceivingForm
